import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TopBar from "./features/sm/shell/TopBar.jsx";
import { usePhone } from "./features/sm/phone/usePhone.js";
import { PhoneShellProvider } from "./features/sm/phone/PhoneShell.jsx";
import PhoneSignIn from "./features/sm/phone/PhoneSignIn.jsx";
import Today from "./features/sm/screens/Today.jsx";
import Plan from "./features/sm/screens/Plan.jsx";
import EndOfDay from "./features/sm/screens/EndOfDay.jsx";
import { shapeEndOfDay, closeSide, submitEndOfDay, nextWorkingDay } from "./features/sm/api/endOfDay.js";
import { filterPlanForecast, filterPlanRepairChecks, buildForecastBookRow } from "./features/sm/api/planForecast.js";
import NCR from "./features/sm/screens/NCR.jsx";
import Report from "./features/sm/screens/Report.jsx";
import FlowGallery from "./features/sm/screens/report/FlowGallery.jsx";
import ErrorState from "./features/sm/screens/ErrorState.jsx";
import { createApiClient } from "./features/sm/api/client.js";
import { runImportCommit } from "./features/sm/api/importCommit.js";
import {
  shapeTodayScreen,
  shapeDayScreen,
  topBarDayLabel,
  applyDayOverrides,
  buildDayAssignItems,
  shapeReportsScreen,
  toTodayViewModel,
  shapePlanScreen,
  computeBacklogPaging,
  fetchBacklogPagesUntilMatch,
  lookupWithCacheRefresh,
  findRegisterRowByInspectionNumber,
  findNcrForInspection,
  buildDragUpsertRow,
  buildBookingRow,
  describePlanInvalid,
  describePlanUpsertFailure,
  shapeNcrScreen,
  buildDescriptionPatch,
  buildReturnPatch,
  buildDismissPatch,
  buildRaiseBody,
  buildResolveBody,
  newRaiseId,
  shapeReportPage,
  clampReportWeekForward,
  buildReportConfirmBody,
  buildReportPatchBody,
  applyRowToEnvelope,
  filterByProjectCodes,
  filterPlanLanes,
  singleSelectedProjectCode,
  shapeImportCounts,
  IMPORT_REFUSED_MESSAGE,
  describeImportRefusal,
  shapeImportCandidates,
  defaultImportSelection,
} from "./features/sm/api/shape.js";
import { loadStoredSelectedCodes, storeSelectedCodes, pruneSelectedCodes } from "./features/sm/api/projectFilterStorage.js";
import {
  readPageHash,
  writePageHash,
  planLoadFailureOutcome,
  createPlanCommitGate,
  decideTodayLoad,
  todayRefreshDue,
  todayRefreshFailureNotice,
} from "./features/sm/api/pageState.js";
import { fixtureMainEnvelope, fixtureReportsEnvelope, fixtureMainMeta } from "./features/sm/fixtures/main.js";
import { dayEnvelopeFor, applyEndOfDayWrite, FIXTURE_DAY_ME } from "./features/sm/fixtures/day.js";
import { createFixturePlanStore, fixtureImportRuns, planFixtureWorld, FC_REGISTER, FC_RECORD_EXTRAS, applyFixtureReview } from "./features/sm/fixtures/plan.js";
import { recordReviewView, buildReviewItem, readReviewResult } from "./features/sm/api/recordReview.js";
import { loadRecord, loadFullPhoto } from "./features/sm/api/recordLoad.js";
import { fixtureNcrProjects, createFixtureNcrStore } from "./features/sm/fixtures/ncr.js";
import { fixtureReportPageEnvelope, fixtureReportPeriodPageEnvelope, fixtureReportWeekStart, createFixtureReportStore } from "./features/sm/fixtures/report.js";
import { periodKey, parsePeriodKey, isPeriodErrorCode } from "./features/sm/api/reportPeriod.js";
import { buildFixtureV2Envelope, V2_PERIOD, V2_TODAY, V2_CURRENT_WEEK, V2_ME, V2_STATES } from "./features/sm/fixtures/report-v2.js";
import { API_BASE, fixtureModeFromLocation, fixtureStateFromLocation, themeFromLocation } from "./config.js";
import {
  applyOptimisticMove,
  applyOptimisticCreate,
  reconcileUpsertReadback,
  createPlanOptimistic,
  patchReportReadiness,
  createReportWeekCache,
  applyReadinessUpdate,
  createGenerationGuard,
  decideWeekCommit,
  planResponseDisposition,
  isQuietNcrRefresh,
  startNcrPolling,
  rosterForProject,
} from "./features/sm/api/speed.js";
import * as auth from "./features/sm/auth.js";

/** The Monday of the ISO week containing `date` (client-local calendar day). */
function mondayOf(date = new Date()) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dow = d.getUTCDay();
  const diff = (dow + 6) % 7; // 0 for Monday
  d.setUTCDate(d.getUTCDate() - diff);
  return d.toISOString().slice(0, 10);
}

function addDays(day, count) {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + count);
  return d.toISOString().slice(0, 10);
}

function addWeeks(weekStart, count) {
  const d = new Date(`${weekStart}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + count * 7);
  return d.toISOString().slice(0, 10);
}

function weekLabelNow(date = new Date()) {
  const day = date.toISOString().slice(0, 10);
  const weekday = date.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
  const dayOfMonth = date.getUTCDate();
  const month = date.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" });
  // ISO week number.
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  target.setUTCDate(target.getUTCDate() + 4 - (target.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
  return `W${weekNo} · ${weekday} ${dayOfMonth} ${month}`;
}

/**
 * Filters a raw cockpit envelope's `projects`/`errors` (and `counts`, which
 * is dropped) down to the top bar's selected project codes — applied BEFORE
 * shaping, never after, so every number the shaped model derives (the
 * Today lead's "N items … across M projects", the readiness/report side
 * cards, nav badges) is computed from the exact rows the screen renders.
 * Filtering the already-shaped view model instead (the previous approach)
 * left the lead text and nav counts reading the full, unfiltered total
 * while only the rows were filtered — the live "994 items … 4 rows shown"
 * defect. `counts` is the backend's own unfiltered total (GET /api/cockpit/
 * today §counts): once projects are filtered client-side that field would
 * disagree with the filtered rows, so it is dropped and shapeTodayScreen's
 * own fallback recomputes it from the filtered groups instead.
 */
function filterEnvelopeByCodes(envelope, selectedCodes) {
  if (!envelope) return envelope;
  return {
    ...envelope,
    projects: filterByProjectCodes(envelope.projects, selectedCodes),
    errors: filterByProjectCodes(envelope.errors, selectedCodes),
    counts: undefined,
  };
}

export default function App() {
  // Phone layout (viewport width 640px and below): the same data and handlers,
  // drawn as mockups/cockpit-v3/mobile/ - bottom tab bar, cards, sheets.
  const phone = usePhone();
  const fixture = useMemo(() => fixtureModeFromLocation(), []);
  const planWorld = useMemo(() => planFixtureWorld(fixture), [fixture]);
  const theme = useMemo(() => themeFromLocation(), []);
  // ONE current-week source for the whole Report screen (Codex review,
  // App.jsx:770 — the header's "this week" comparison and the Next-week
  // clamp / "Back to this week" handlers previously used two different
  // clocks: the header read the fixture's own frozen week, the handlers
  // always called the real wall-clock `mondayOf()`. In fixture mode those
  // disagree whenever the test run's real date has moved past the fixture's
  // frozen sample week (always, in practice) — "Back to this week" would
  // jump to a week the fixture's own data (and the top bar's own "W36 ·
  // Wed 2 Sep") never describes. `fixturePlanToday` is the same frozen
  // "today" Plan's own today-highlighting already uses; deriving every
  // current-week reference from it keeps Plan, Today and Report agreeing.
  //
  // Held as STATE, not a `useMemo(() => ..., [fixture])` (codex review
  // round 2, App.jsx:126): a memo computes once at mount and never again —
  // `fixture` never changes after mount, so nothing would ever re-run it. A
  // site manager who leaves the tab open across Monday 00:00 UTC would keep
  // reading last week as "current" indefinitely. The effect below
  // recomputes it on window focus (the tab was asleep, they switch back)
  // and on a once-a-minute tick (a tab that stays focused straight through
  // the rollover still crosses it); fixture mode's clock stays frozen —
  // the effect bails out immediately rather than installing either.
  // `?fixture=report-v2` is the Weekly report v2 sample world: today is
  // Sat 10 Oct 2026 and the state of the design to show is `&state=`.
  const reportV2 = fixture === "report-v2";
  const reportV2State = useMemo(() => {
    const s = reportV2 ? fixtureStateFromLocation() : "draft";
    return V2_STATES.includes(s) ? s : "draft";
  }, [reportV2]);
  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    reportV2 ? V2_CURRENT_WEEK : fixture ? mondayOf(new Date(`${planWorld.today}T00:00:00Z`)) : mondayOf()
  );
  useEffect(() => {
    if (fixture) return; // the fixture world's clock never ticks — frozen at fixturePlanToday
    function recompute() {
      setCurrentWeekStart((prev) => {
        const next = mondayOf();
        return next === prev ? prev : next;
      });
    }
    recompute();
    window.addEventListener("focus", recompute);
    const intervalId = setInterval(recompute, 60000);
    return () => {
      window.removeEventListener("focus", recompute);
      clearInterval(intervalId);
    };
  }, [fixture]);
  // Reload-survival (owner live-use defect: a reload always went back to
  // Today and refetched everything from scratch) — read once at mount,
  // never in fixture/artboard mode, which must always render its own fixed
  // screenshot state regardless of whatever hash a prior run left behind.
  const pageHash = useMemo(() => (fixture ? {} : readPageHash()), [fixture]);
  // The fixture value also selects which screen an artboard-conformance
  // screenshot lands on (v-artboard.js loads `?fixture=<name>` and
  // screenshots whatever is showing) — "plan" opens straight to Plan, every
  // other fixture value (or none, in the live app) opens on Today.
  const [screen, setScreen] = useState(
    fixture === "plan" || fixture === "plan-forecast" ? "plan" : fixture === "end-of-day" ? "endofday" : fixture === "ncr" ? "ncrs" : fixture === "report" || fixture === "report-period" || fixture === "report-v2" ? "weekly" : pageHash.screen || "today"
  );
  const [signedIn, setSignedIn] = useState(Boolean(fixture));
  const [identity, setIdentity] = useState(fixture ? fixtureMainMeta.identity : { initials: "?", role: "Site manager" });
  const [selectedCodes, setSelectedCodes] = useState(() =>
    fixture ? [] : pageHash.selectedCodes !== undefined ? pageHash.selectedCodes : loadStoredSelectedCodes()
  );
  const [todayEnvelope, setTodayEnvelope] = useState(null);
  // Fixture mode has no server: End of day writes are laid over the fixture envelope here.
  const [fixtureWritten, setFixtureWritten] = useState(null);
  const [reportsEnvelope, setReportsEnvelope] = useState(null);
  const [reportsLoadError, setReportsLoadError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [weekStart, setWeekStart] = useState(() => (fixture ? planWorld.weekStart : pageHash.weekStart || mondayOf()));
  const weekStartRef = useRef(weekStart);
  weekStartRef.current = weekStart;
  const planCacheRef = useRef(new Map()); // weekStart -> envelope, in-memory per session; never cross-tab
  const planRequestRef = useRef(0); // generation counter — only the LATEST in-flight loadPlan call is ever applied
  // Per-week commit-ordering gate (live-journey release-blocker regression,
  // runs 37597768032/37597701646) — see pageState.js#createPlanCommitGate.
  const planCommitGateRef = useRef(createPlanCommitGate());
  // Count of write (drag/import) read-backs currently in flight — the
  // prefetch effect below refuses to START a new background fetch while
  // this is non-zero, so a write's round trip is never left competing
  // against fresh prefetch traffic for the same connection/server capacity
  // (the other half of the same regression: prefetching never touches the
  // written week directly, but the commit gate alone does not prevent a
  // slow-to-START prefetch from simply never finishing in good time). A
  // COUNT, not a boolean tied to generation ownership (codex review,
  // PR #1963 P2a) — a superseded write's own `finally` must still release
  // ITS OWN count, independent of whichever call currently owns
  // `planRequestRef`, or an overlap with a non-write navigation could
  // leave this stuck permanently non-zero.
  const planWriteInFlightRef = useRef(0);
  const [planEnvelope, setPlanEnvelope] = useState(null);
  // Latest displayed Plan envelope, readable from async handlers without a
  // stale closure (optimistic moves compare against it to decide whether a
  // revert/reconcile still applies to what is on screen).
  const planEnvelopeRef = useRef(planEnvelope);
  planEnvelopeRef.current = planEnvelope;
  function showPlan(env) {
    planEnvelopeRef.current = env;
    setPlanEnvelope(env);
  }
  const planOptimistic = useMemo(
    () =>
      createPlanOptimistic({
        getEnvelope: () => planEnvelopeRef.current,
        setEnvelope: (env) => {
          planEnvelopeRef.current = env;
          setPlanEnvelope(env);
        },
        cache: planCacheRef.current,
        gate: planCommitGateRef.current,
        onInFlight: (delta) => {
          planWriteInFlightRef.current = Math.max(0, planWriteInFlightRef.current + delta);
        },
      }),
    []
  );
  // The week currently being fetched over the network (not a cache hit) —
  // drives Plan's "Loading week N…" + disables the W±1 buttons so repeated
  // clicks can never skip weeks (owner live-use defect: a 10-20s week
  // switch with no feedback led to clicking through several weeks).
  const [planLoadingWeek, setPlanLoadingWeek] = useState(null);
  // The currently-displayed week IS cached data, but its background
  // revalidation (a plain nav cache hit, not a post-write read-back) came
  // back failed — the cached envelope stays on screen (still probably
  // fine) but is marked stale with a retry affordance, rather than either
  // hiding the failure or discarding good cached data for a full error
  // screen (codex review finding, PR #1957).
  const [planStaleWeek, setPlanStaleWeek] = useState(null);
  const [planLoadError, setPlanLoadError] = useState(null);
  // The week the standing Plan error belongs to: an error for week N is never
  // left standing over a different week.
  const planErrorWeekRef = useRef(null);
  const [pinnedBooking, setPinnedBooking] = useState(null);
  const [planFocus, setPlanFocus] = useState(null);
  const [planNotice, setPlanNotice] = useState(null);
  // The scheduleRef of the row currently being written by a drag/drop
  // (owner feedback: the moved tile showed nothing while its write was in
  // flight, which — on a slow connection — reads the same as a drag that
  // silently did nothing). Set the instant the drop handler starts, before
  // the POST is even issued, so the tile shows "Saving…" immediately;
  // cleared once the write's own read-back settles, success or failure.
  const [planSavingRef, setPlanSavingRef] = useState(null);
  const forecastBookingRef = useRef(new Set()); // "code:scheduleRef" of forecast items being booked right now
  // "To book" backlog paging (owner-approved Plan redesign, DEC-CHAT-
  // 20261007-412 — planView pages the backlog 20 at a time, PR #1958 §4.3).
  // `planEnvelope.backlog` is always page 1; `backlogExtra` accumulates any
  // further pages the site manager asked for via "Show N more", reset on a
  // week switch (a different week's backlog is a different list).
  const [backlogExtra, setBacklogExtra] = useState([]);
  const [backlogLoadingMore, setBacklogLoadingMore] = useState(false);
  useEffect(() => {
    setBacklogExtra([]);
  }, [weekStart]);
  // Plan detail panel photos/NCR (codex review gap-closure — no backend
  // change): `planDetailIdentity` is what Plan.jsx's own selection resolves
  // to ({ code, inspectionNumber } | null).
  const [planDetailIdentity, setPlanDetailIdentity] = useState(null);
  const [planDetailExtras, setPlanDetailExtras] = useState(null);
  const planPhotoUrlsRef = useRef([]);
  // Per-project caches for the register/NCR reads the detail panel needs —
  // codex review round 2: "each selection triggers whole-project register
  // and NCR reads" — every OTHER selection within the same project (and the
  // same project's own re-inspection/unbooked rows) reuses these for the
  // life of the Plan view, rather than re-fetching the whole project on
  // every click. Only a SUCCESSFUL read is ever cached — a failed one must
  // be retried, never remembered as "the project has no rows". Invalidated
  // by `invalidatePlanDetailCache` on any write that could change the
  // underlying data (handlePlanDrop/handlePlanDropPinned/handleImportCommit).
  const planRegisterCacheRef = useRef(new Map()); // code -> register rows
  const planNcrCacheRef = useRef(new Map()); // code -> ncr rows
  // Identifies the in-flight (or most recently completed) detail request —
  // a response for a SUPERSEDED selection (the site manager clicked
  // elsewhere before this one resolved) must never overwrite the newer
  // one's state.
  const planDetailRequestRef = useRef(null);

  function invalidatePlanDetailCache(code) {
    planRegisterCacheRef.current.delete(code);
    planNcrCacheRef.current.delete(code);
  }

  // Codex review round 3 (App.jsx:220): the per-project caches used to be
  // invalidated ONLY by this session's own writes — a Plan refresh that
  // picked up someone ELSE's change (another site manager, the capture
  // app, or this project's own schedule import from a different tab) never
  // cleared them, so a newly-recorded inspection stayed invisible to the
  // detail panel for the life of the view. Clearing on every `planEnvelope`
  // change (every successful refetch AND every week-switch cache-hit
  // re-render) means the caches are never older than whatever Plan itself
  // is currently showing.
  useEffect(() => {
    planRegisterCacheRef.current.clear();
    planNcrCacheRef.current.clear();
  }, [planEnvelope]);

  async function fetchAndCacheRegisterRows(code) {
    const result = await api.fetchInspectionsRange(code);
    return result.ok && Array.isArray(result.body) ? { ok: true, rows: result.body } : { ok: false, rows: [] };
  }

  async function fetchAndCacheNcrRows(code) {
    const result = await api.fetchNcrs(code);
    return result.ok && Array.isArray(result.body) ? { ok: true, rows: result.body } : { ok: false, rows: [] };
  }

  /**
   * Fetches the detail panel's photos/NCR for `identity`. `forceRefresh`
   * (the panel's own "Retry") first drops this project's cache so a
   * retry is a REAL re-read, never a replay of the same failure.
   * `registerError`/`ncrError`/`photosError` are reported separately —
   * codex review round 2: a failed read must render "Could not load
   * photos — Retry" / "NCR status unknown — Retry", never silently fall
   * through to the genuinely-empty state ("No photos recorded", no Open
   * NCR button).
   */
  async function loadPlanDetailExtras(identity, forceRefresh) {
    const requestKey = `${identity.code}:${identity.inspectionNumber}`;
    planDetailRequestRef.current = requestKey;
    if (forceRefresh) invalidatePlanDetailCache(identity.code);
    setPlanDetailExtras({ photos: [], photosError: false, ncr: null, ncrError: false, loading: true });
    // `lookupWithCacheRefresh` (shape.js, codex review round 3) is what
    // makes a cache HIT that doesn't contain this specific inspection
    // refetch once before concluding it is absent — a plain `cache.has`
    // check (round 2) could never do that.
    const [registerLookup, ncrLookup] = await Promise.all([
      lookupWithCacheRefresh({
        cache: planRegisterCacheRef.current,
        code: identity.code,
        find: (rows) => findRegisterRowByInspectionNumber(rows, identity.inspectionNumber),
        fetchFresh: () => fetchAndCacheRegisterRows(identity.code),
      }),
      lookupWithCacheRefresh({
        cache: planNcrCacheRef.current,
        code: identity.code,
        find: (rows) => findNcrForInspection(rows, identity.inspectionNumber),
        fetchFresh: () => fetchAndCacheNcrRows(identity.code),
      }),
    ]);
    if (planDetailRequestRef.current !== requestKey) return; // superseded by a newer selection
    const registerRow = registerLookup.ok ? registerLookup.value : null;
    const ncrRow = ncrLookup.ok ? ncrLookup.value : null;
    const ncr = ncrRow ? { code: identity.code, ncrNumber: ncrRow.ncrNumber, title: ncrRow.title || "" } : null;
    let photos = [];
    let photosError = !registerLookup.ok;
    if (registerLookup.ok && registerRow && registerRow.clientCaptureId) {
      const photosResult = await api.fetchCapturePhotos(identity.code, registerRow.clientCaptureId);
      if (planDetailRequestRef.current !== requestKey) return;
      if (!photosResult.ok || !Array.isArray(photosResult.body)) {
        photosError = true;
      } else {
        // Lazy, bounded to 3 at first (codex review round 2) — a thumbnail
        // is its own round trip, and the detail panel is a small fixed-
        // width card, not a gallery; the remaining photos (if any) are
        // simply not fetched rather than all loaded up front.
        const loaded = await Promise.all(
          photosResult.body.slice(0, 3).map(async (p) => {
            const blobResult = await api.fetchPhotoThumbnailBlob(identity.code, p.clientPhotoId, p.driveItemId);
            if (planDetailRequestRef.current !== requestKey || !blobResult.ok || !blobResult.blob) return null;
            const url = URL.createObjectURL(blobResult.blob);
            planPhotoUrlsRef.current.push(url);
            return { key: p.clientPhotoId, url, caption: p.caption || "" };
          })
        );
        if (planDetailRequestRef.current !== requestKey) return;
        photos = loaded.filter(Boolean);
      }
    }
    const record = registerRow ? recordReviewView(registerRow, planRegisterCacheRef.current.get(identity.code) || []) : null;
    setPlanDetailExtras({ photos, photosError, ncr, ncrError: !ncrLookup.ok, record, loading: false });
  }

  // `?fixture=plan-forecast` shows the record detail from an in-memory register, so the approve / reopen / repair-link buttons can be driven offline.
  const forecastFixture = fixture === "plan-forecast";
  const [fixtureRegister, setFixtureRegister] = useState(() => (forecastFixture ? FC_REGISTER.map((r) => ({ ...r })) : []));
  // The same rows, readable at once: the record sheet reloads right after a fixture review is applied.
  const fixtureRegisterRef = useRef(fixtureRegister);
  fixtureRegisterRef.current = fixtureRegister;

  // "Open record": the full record, read only. The loader and the photo reader are
  // handed to Plan as stable functions (the sheet reloads when they change); blob
  // URLs made for the sheet are freed when it closes.
  const recordUrlsRef = useRef([]);
  const makeRecordUrl = (blob) => {
    const url = URL.createObjectURL(blob);
    recordUrlsRef.current.push(url);
    return url;
  };
  function releaseRecordUrls() {
    recordUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    recordUrlsRef.current = [];
  }
  async function readRecord(identity) {
    if (fixture) {
      // Offline checks: `window.__fixtureRecordFail = n` makes the next n record reads fail.
      if (window.__fixtureRecordFail > 0) {
        window.__fixtureRecordFail -= 1;
        return { ok: false, errorCode: "register_unavailable" };
      }
      const rows = fixtureRegisterRef.current;
      const row = rows.find((r) => Number(r.inspectionNumber) === Number(identity.inspectionNumber));
      if (!row) return { ok: false, errorCode: "not_found" };
      const extras = FC_RECORD_EXTRAS[row.inspectionNumber] || {};
      const photos = (extras.photos || []).map((p) => ({ key: p.clientPhotoId, photoId: p.clientPhotoId, driveItemId: p.driveItemId, caption: p.caption, capturedAtUtc: p.capturedAtUtc, url: p.url }));
      return { ok: true, row, rows, photos, photosError: false, amendments: extras.amendments || [], amendmentsError: false, ncr: extras.ncr || null, ncrError: false };
    }
    return loadRecord({ api, identity, makeUrl: makeRecordUrl });
  }
  async function readRecordPhoto(identity, photo) {
    if (fixture) {
      if (window.__fixturePhotoFail > 0) {
        window.__fixturePhotoFail -= 1;
        return { ok: false, url: "" };
      }
      return { ok: Boolean(photo.url), url: photo.url };
    }
    return loadFullPhoto({ api, code: identity.code, photo, makeUrl: makeRecordUrl });
  }
  const readRecordRef = useRef(readRecord);
  readRecordRef.current = readRecord;
  const readRecordPhotoRef = useRef(readRecordPhoto);
  readRecordPhotoRef.current = readRecordPhoto;
  const stableReadRecord = useCallback((identity) => readRecordRef.current(identity), []);
  const stableReadRecordPhoto = useCallback((identity, photo) => readRecordPhotoRef.current(identity, photo), []);
  const stableReleaseRecord = useCallback(() => releaseRecordUrls(), []);
  useEffect(() => {
    if (forecastFixture) {
      if (!planDetailIdentity) {
        setPlanDetailExtras(null);
        return;
      }
      const row = fixtureRegister.find((r) => Number(r.inspectionNumber) === Number(planDetailIdentity.inspectionNumber));
      setPlanDetailExtras({ photos: [], photosError: false, ncr: null, ncrError: false, record: row ? recordReviewView(row, fixtureRegister) : null, loading: false });
      return;
    }
    if (fixture || !planDetailIdentity) {
      setPlanDetailExtras(null);
      planDetailRequestRef.current = null;
      return;
    }
    loadPlanDetailExtras(planDetailIdentity, false);
    return () => {
      planPhotoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      planPhotoUrlsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixture, planDetailIdentity, fixtureRegister]);
  const [importPanel, setImportPanel] = useState(null);
  const fixturePlanStore = useMemo(() => (fixture ? createFixturePlanStore({ world: planWorld.name }) : null), [fixture]);
  const [ncrProjects, setNcrProjects] = useState(() => (fixture ? fixtureNcrProjects.projects : null));
  const [ncrErrors, setNcrErrors] = useState(() => (fixture ? fixtureNcrProjects.errors : []));
  const [ncrsLoadError, setNcrsLoadError] = useState(null);
  const ncrProjectsRef = useRef(null);
  ncrProjectsRef.current = ncrProjects;
  const loadNcrsRef = useRef(null);
  // Any NCR write or reload bumps this; an older load's response (e.g. a poll
  // that started before a write) is dropped, never applied over newer data.
  const ncrGuardRef = useRef(createGenerationGuard());
  const [selectedNcrKey, setSelectedNcrKey] = useState(null);
  const fixtureNcrStore = useMemo(() => (fixture ? createFixtureNcrStore() : null), [fixture]);
  const [reportWeekStart, setReportWeekStart] = useState(() => (reportV2 ? V2_CURRENT_WEEK : fixture ? fixtureReportWeekStart : mondayOf()));
  const [reportEnvelope, setReportEnvelope] = useState(() =>
    fixture === "report" ? fixtureReportPageEnvelope : fixture === "report-period" ? fixtureReportPeriodPageEnvelope : reportV2 ? buildFixtureV2Envelope(reportV2State) : null
  );
  const [reportPageLoadError, setReportPageLoadError] = useState(null);
  const reportWeekCacheRef = useRef(createReportWeekCache()); // weekStart -> envelope, in-memory per session
  const reportEnvelopeRef = useRef(reportEnvelope);
  reportEnvelopeRef.current = reportEnvelope;
  const reportWeekStartRef = useRef(reportWeekStart);
  reportWeekStartRef.current = reportWeekStart;
  // SPEC-CIS-REPORT-CUSTOM-PERIOD-001 section 4.5: a custom period on screen
  // replaces the week. The VIEW key (a week's Monday, or "start..end") is
  // what the per-view cache, the refs and the stale-response guards key on.
  const [reportPeriod, setReportPeriod] = useState(() => (reportV2 ? { ...V2_PERIOD } : null));
  const [reportPeriodError, setReportPeriodError] = useState(null);
  const reportPeriodRef = useRef(reportPeriod);
  reportPeriodRef.current = reportPeriod;
  const reportViewKey = reportPeriod ? periodKey(reportPeriod) : reportWeekStart;
  const reportViewKeyRef = useRef(reportViewKey);
  reportViewKeyRef.current = reportViewKey;
  const reportRequestRef = useRef(0); // generation counter — only the LATEST report request is applied
  const [reportPageLoading, setReportPageLoading] = useState(false);
  const fixtureReportStore = useMemo(
    () => (fixture ? createFixtureReportStore({ period: fixture === "report-period", dataset: reportV2 ? "v2" : "v1", state: reportV2State }) : null),
    [fixture, reportV2, reportV2State]
  );
  const [initError, setInitError] = useState(null);
  const [initAttempt, setInitAttempt] = useState(0);
  // False while auth.initBounded() (MSAL initialize() + handleRedirectPromise())
  // is in flight, including on a retry. Gates the "Sign in" button so it
  // never renders (and so can never be clicked) before MSAL has actually
  // initialized — the DOM also carries this as `data-app-ready` for the
  // browser-test broker (scripts/ceo-cockpit-swa-session-broker.js), which
  // must never click the control during this window.
  const [initReady, setInitReady] = useState(false);

  function handleNavigate(nextScreen, payload) {
    setScreen(nextScreen);
    if (nextScreen === "plan" && payload && payload.openItem) {
      // "Open in Plan" (owner retest 3): that item's week, item selected.
      const { code, scheduleRef, plannedDate } = payload.openItem;
      if (plannedDate) setWeekStart(mondayOf(new Date(`${plannedDate}T00:00:00Z`)));
      setPlanFocus({ code, scheduleRef, nonce: Date.now() });
    } else if (nextScreen === "plan" && payload) setPinnedBooking(payload);
    if (nextScreen === "ncrs" && payload) setSelectedNcrKey(`${payload.code}-${payload.ncrNumber}`);
    if (nextScreen === "weekly" && payload && payload.weekStart) {
      setReportWeekStart(payload.weekStart);
      setReportPeriod(null);
      setReportPeriodError(null);
    }
  }

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Bounded so the initial sign-in gate can never hang (review round 2,
  // point 4): initBounded() resolves within its own timeout regardless of
  // what auth.init() (MSAL initialize + handleRedirectPromise) does. A
  // failure/timeout renders an ErrorState in place of the plain "Sign in"
  // button below, since that button's own onClick awaits the SAME hung
  // init() and would otherwise do nothing forever with no feedback.
  useEffect(() => {
    if (fixture) {
      setInitReady(true);
      return;
    }
    let cancelled = false;
    setInitReady(false);
    (async () => {
      const result = await auth.initBounded();
      if (cancelled) return;
      if (!result.ok) {
        setInitError(result.errorCode);
        setInitReady(true);
        return;
      }
      setInitError(null);
      if (auth.isSignedIn()) {
        setSignedIn(true);
        setIdentity({ initials: auth.initialsFrom(auth.getDisplayName()), role: "Site manager" });
      }
      setInitReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [fixture, initAttempt]);

  const api = useMemo(
    () => createApiClient({ apiBase: API_BASE, getToken: fixture ? async () => null : auth.getToken }),
    [fixture]
  );

  // Fixture mode swaps only the transport: the fixtures are raw envelopes in
  // the exact shapes GET /api/cockpit/today and /reports return, and run
  // through the same shape.js pipeline the live path uses. Declared here
  // (rather than nearer its Today-specific consumers) because Plan and NCR
  // loading below also need `projectCodes`.
  const fixtureTodayEnvelope = useMemo(
    () => (fixture ? dayEnvelopeFor(["day-busy", "day-empty", "day-20", "end-of-day"].includes(fixture) ? fixture : fixture === "plan-forecast" ? "end-of-day" : "main", fixtureMainEnvelope) : null),
    [fixture]
  );
  const envelope = fixture ? fixtureWritten || fixtureTodayEnvelope : todayEnvelope;
  const projectCodes = ((envelope && envelope.projects) || []).map((p) => p.code).sort();
  const projectCodesKey = projectCodes.join("|");

  // Drop any persisted/previously-selected code the server's project list no
  // longer carries (project removed/un-admitted) — never run before
  // projectCodes has actually loaded, or the empty first render would wipe
  // a just-restored selection before it ever gets the chance to validate.
  useEffect(() => {
    if (!projectCodesKey) return;
    setSelectedCodes((prev) => pruneSelectedCodes(prev, projectCodes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectCodesKey]);

  // Persist the top bar's selection per browser so it survives a reload
  // (owner live-use defect 2026-10-06) — never in fixture/artboard mode,
  // which must always render its own fixed, deterministic selection.
  useEffect(() => {
    if (fixture) return;
    storeSelectedCodes(selectedCodes);
  }, [fixture, selectedCodes]);

  // Mirrors the active tab, the Plan week and the selected projects into the
  // page address so a reload restores the same view (owner live-use defect
  // 2026-10-06) — never in fixture/artboard mode. `replaceState`, never a
  // new history entry per change.
  useEffect(() => {
    if (fixture) return;
    writePageHash({ screen, weekStart, selectedCodes });
  }, [fixture, screen, weekStart, selectedCodes]);

  const todayGenRef = useRef(0);
  /**
   * `quiet` (the refresh after a write): a failure never replaces Today with the
   * error screen; the current screen stays and a small notice says it is not
   * up to date. Resolves true when the read landed.
   */
  const todayEnvelopeRef = useRef(null);
  const lastAppliedGenRef = useRef(0);
  const todayReadAtRef = useRef(0);
  async function loadToday({ quiet = false, background = false, date } = {}) {
    const myGen = ++todayGenRef.current;
    const result = await api.fetchToday(date);
    // decideTodayLoad (pageState.js, unit-tested): never store a non-envelope,
    // never leave the screen on "Loading…" because a newer read failed quietly,
    // never let an older read overwrite a newer one already applied.
    const decision = decideTodayLoad({ result, quiet, superseded: myGen !== todayGenRef.current, hasEnvelope: Boolean(todayEnvelopeRef.current), gen: myGen, lastAppliedGen: lastAppliedGenRef.current });
    if (decision.action === "apply") {
      lastAppliedGenRef.current = myGen; // synchronous: before React re-renders
      todayEnvelopeRef.current = decision.body;
      todayReadAtRef.current = Date.now();
      setTodayEnvelope(decision.body);
      setLoadError(null);
      if (quiet) setDayNotice(null);
      return true;
    }
    if (decision.action === "ignore") return true; // a newer load owns the screen
    if (decision.action === "notice") {
      // After a write the data was saved; a background refresh saved nothing.
      setDayNotice(todayRefreshFailureNotice({ background }));
      return false;
    }
    setLoadError(decision.errorCode);
    return false;
  }

  useEffect(() => {
    if (fixture || !signedIn) return;
    let cancelled = false;
    (async () => {
      await loadToday();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixture, signedIn, api]);

  // The "Week NN reports" side card needs the report archive, not just
  // readiness, so it is a second read (GET /api/cockpit/reports) run in
  // parallel with Today. A per-project failure inside a 200 (envelope.errors)
  // is handled by shapeReportsScreen itself (an "Unavailable" card row, same
  // treatment as Today's errors[]); a failure of the whole call (network
  // error, non-2xx) is tracked here so the card can show its own error state
  // instead of quietly falling back to "loading" forever.
  useEffect(() => {
    if (fixture || !signedIn) return;
    let cancelled = false;
    (async () => {
      const result = await api.fetchReports();
      if (cancelled) return;
      if (result.ok) {
        setReportsEnvelope(result.body);
        setReportsLoadError(null);
      } else {
        setReportsLoadError(result.body && result.body.errorCode ? result.body.errorCode : `http_${result.status}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fixture, signedIn, api]);

  // Plan is only read when the screen is visible (or has been), since it
  // takes its own weekStart and re-reads after every write (read-back,
  // never the optimistic position — SPEC-CIS-SITE-MANAGER-COCKPIT-V3-001
  // §4.3). Fixture mode goes through the in-memory fixture store instead of
  // the network client, same transport-only swap as Today's fixture path.
  /**
   * Week-switch speed (owner live-use defect: W40/W42 took 10-20s with no
   * feedback, so repeated clicks skipped several weeks). `planCacheRef`
   * holds every week's envelope already read THIS session, keyed by
   * `weekStart` — a cache hit renders synchronously, with no network wait,
   * then still revalidates in the background so the screen never shows
   * stale data forever. `planRequestRef` is a generation counter: clicking
   * through several weeks before the first response lands no longer races
   * — only the response matching the LATEST call's generation is ever
   * applied, so a fast W40→W41→W42 click sequence lands on W42, never
   * flickers back to an in-flight W40/W41 response arriving late.
   * `write` controls whether this call's result replaces `planEnvelope` at
   * all — a background prefetch (see the effect below) only POPULATES the
   * cache, it never touches what is currently on screen.
   *
   * `isWrite` marks a post-write read-back (drag/drop, import commit —
   * SPEC-CIS-SITE-MANAGER-COCKPIT-V3-001 §4.3: the screen must always show
   * the server's read-back position, never the pre-write state). Codex
   * review finding (PR #1957, HIGH): the cache previously let a failed
   * read-back silently keep the pre-write envelope on screen with no error
   * at all, because a cache hit suppressed the error branch below
   * regardless of WHY this call was made. `isWrite` forces the cache (and
   * its sessionStorage snapshot) to be invalidated BEFORE the fetch, so a
   * write's own read-back can never be served from — or silently judged
   * against — stale pre-write data: it either lands on the fresh
   * read-back, or it surfaces as a real, actionable error.
   *
   * Live-journey release-blocker regression (runs 37597768032/
   * 37597701646, PR #1957): a drag's write read-back landed correctly,
   * but a background prefetch for the SAME week — started earlier, while
   * still viewing the prior week, and slow enough to still be in flight —
   * resolved AFTER the write and silently overwrote the cache and the
   * sessionStorage snapshot with its own stale, pre-write data; a reload
   * right after the drag then re-seeded its initial render from exactly
   * that clobbered snapshot. Neither the generation counter above
   * (write-only, guards the DISPLAYED envelope against a superseded
   * NAVIGATION) nor `isWrite`'s own cache deletion (only protects what
   * existed BEFORE this call's fetch starts) guarded against an OLDER
   * call's result committing AFTER a newer one finishes — `planCommitGateRef`
   * (pageState.js#createPlanCommitGate) closes that gap: every call is
   * stamped at CALL time (`start()`, before its fetch is even issued), and
   * a result may only be written into the cache/sessionStorage for its
   * week if no later-started call for that same week already has
   * (`tryCommit`) — so a slow prefetch can never clobber a write, no
   * matter how long its own round trip takes.
   */
  async function loadPlan(forWeek, { write = true, isWrite = false, quiet = false } = {}) {
    const cache = planCacheRef.current;
    if (isWrite) {
      cache.delete(forWeek);
      planWriteInFlightRef.current += 1;
    }
    const cached = cache.get(forWeek);
    // Only a `write` call bumps the shared generation counter — a
    // background prefetch must never invalidate a genuine concurrent
    // navigation request (it would otherwise make a real W40→W41 click's
    // own response get dropped as "superseded" by an unrelated prefetch).
    // A QUIET refetch (after an optimistic write) never bumps it: it must not
    // supersede a real navigation's response (review: drop on W, click W+1).
    const myGen = write && !quiet ? ++planRequestRef.current : null;
    const myCommitGen = isWrite ? planCommitGateRef.current.startWrite(forWeek) : planCommitGateRef.current.start();
    if (write && !quiet) {
      // A new navigation or retry starts clean: the previous attempt's error
      // never stands over a request that is now in flight.
      setPlanLoadError(null);
      planErrorWeekRef.current = null;
      if (cached) {
        setPlanEnvelope(cached);
        setPlanLoadingWeek(null);
      } else {
        setPlanLoadingWeek(forWeek);
      }
      setPlanStaleWeek(null);
    }
    try {
      const result = fixture ? await fixturePlanStore.fetchPlan(forWeek) : await api.fetchPlan(forWeek);
      const ok = fixture || result.ok;
      const disposition = planResponseDisposition({
        write,
        quiet,
        myGen,
        currentGen: planRequestRef.current,
        forWeek,
        shownWeek: weekStartRef.current,
        errorWeek: planErrorWeekRef.current,
      });
      if (disposition === "drop") return; // superseded by a newer target week — drop this response
      const canCommit = planCommitGateRef.current.tryCommit(forWeek, myCommitGen);
      if (ok && canCommit) {
        // Server data is never cached while a move to this week is still
        // pending, and what is DISPLAYED always has the pending moves
        // re-applied on top, so a refetch landing mid-write cannot clobber
        // them (review findings 1-2).
        if (!planOptimistic.hasPending(forWeek)) {
          cache.set(forWeek, result.body);
        }
        if (disposition === "render") {
          // Error/stale markers clear only when the refetch actually renders.
          showPlan(planOptimistic.overlay(forWeek, result.body));
          setPlanLoadError(null);
          planErrorWeekRef.current = null;
          setPlanStaleWeek(null);
        }
      } else if (ok) {
        // Fetched fine, but a LATER-STARTED call for this exact week has
        // already committed (or will) — this older, slower response must
        // never clobber it. Nothing to do; the newer result stands.
      } else if (planCommitGateRef.current.isStale(forWeek, myCommitGen)) {
        // A newer call for this week already succeeded: this late failure
        // must never replace it.
      } else if (disposition === "render") {
        // A quiet background refetch (after an optimistic write) never blanks
        // the screen: the reconciled data stays, marked not-up-to-date.
        const outcome = quiet ? "stale" : planLoadFailureOutcome({ isWrite, hadCache: Boolean(cached) });
        if (outcome === "error") {
          planErrorWeekRef.current = forWeek;
          setPlanLoadError(result.body && result.body.errorCode ? result.body.errorCode : `http_${result.status}`);
        } else {
          // A plain nav cache hit whose background revalidation failed: the
          // cached envelope (already rendered above) stays on screen, marked
          // stale rather than silently treated as confirmed-current.
          setPlanStaleWeek(forWeek);
        }
      }
    } finally {
      // Codex review finding (PR #1963 P2a): a SUPERSEDED request (the
      // `return` above, myGen !== planRequestRef.current) still ran this
      // `finally` block, wrongly clearing planLoadingWeek even though a
      // NEWER request for a different week might still be genuinely in
      // flight. Only the request that still OWNS the current generation
      // may clear it. `planWriteInFlightRef` is released unconditionally,
      // independent of generation ownership — it is THIS call's own
      // in-flight count, decremented regardless of whether it was
      // superseded (see the ref's own declaration comment).
      if (isWrite) planWriteInFlightRef.current = Math.max(0, planWriteInFlightRef.current - 1);
      if (write && !quiet && myGen === planRequestRef.current) setPlanLoadingWeek(null);
    }
  }

  useEffect(() => {
    if (!signedIn && !fixture) return;
    if (screen !== "plan" && planEnvelope) return; // don't poll while the tab is unvisited; re-read happens on nav/drop
    let cancelled = false;
    (async () => {
      await loadPlan(weekStart);
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixture, signedIn, weekStart, screen]);

  // Prefetches the adjacent weeks in the background once the current week
  // has rendered, so a W±1 click almost always lands on an already-cached
  // week (render in under 1s, per the fix's own target) rather than a fresh
  // 10-20s network wait. `write:false` — this only fills `planCacheRef`,
  // it never replaces whatever is currently on screen. Never STARTS a new
  // target while a write's own read-back is in flight (`planWriteInFlightRef`)
  // — the commit gate above already stops a slow prefetch from clobbering a
  // write's result, but keeping prefetch traffic off the wire for the
  // duration of a write also keeps the write's own round trip from
  // competing with it for the same connection/server capacity (live-journey
  // release-blocker regression, runs 37597768032/37597701646).
  useEffect(() => {
    if (!signedIn && !fixture) return;
    if (screen !== "plan" || !planEnvelope) return;
    let cancelled = false;
    (async () => {
      for (const target of [addWeeks(weekStart, -1), addWeeks(weekStart, 1)]) {
        if (cancelled || planWriteInFlightRef.current) return;
        if (planCacheRef.current.has(target)) continue;
        await loadPlan(target, { write: false });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixture, signedIn, screen, weekStart, planEnvelope]);

  // NCR list has no single cross-project read (unlike Today/Plan): one call
  // per `sm:` project against the existing GET /api/projects/{code}/ncrs,
  // merged client-side (shape.js#shapeNcrScreen), same errors[] partial-
  // failure discipline as Today/Plan. Loaded once projectCodes are known;
  // reloaded after every mutating action (patch/raise/resolve) rather than
  // updating local state optimistically.
  async function loadNcrs({ quiet = false } = {}) {
    const guard = ncrGuardRef.current;
    const myGen = guard.next();
    if (fixture) {
      const projects = await Promise.all(
        fixtureNcrProjects.projects.map(async ({ code }) => {
          const result = await fixtureNcrStore.fetchNcrs(code);
          return { code, rows: result.body };
        })
      );
      if (!guard.isCurrent(myGen)) return;
      setNcrProjects(projects);
      setNcrErrors([]);
      setNcrsLoadError(null);
      return;
    }
    if (!projectCodes.length) return;
    const errors = [];
    const projects = [];
    await Promise.all(
      projectCodes.map(async (code) => {
        const result = await api.fetchNcrs(code);
        if (result.ok) projects.push({ code, rows: result.body });
        else errors.push({ code, error: (result.body && result.body.errorCode) || `http_${result.status}` });
      })
    );
    if (!guard.isCurrent(myGen)) return; // a newer load/write superseded this response
    if (isQuietNcrRefresh({ quiet, hasData: Boolean(ncrProjectsRef.current) })) {
      // Background refresh (focus / 30 s poll): never blank the board. If
      // every project failed keep what is shown; a project that failed this
      // time keeps its previously shown rows instead of flipping to
      // "Unavailable".
      if (projects.length === 0) return;
      const previous = ncrProjectsRef.current || [];
      for (const e of errors) {
        const old = previous.find((p) => p.code === e.code);
        if (old) projects.push(old);
      }
      const stillFailed = errors.filter((e) => !previous.some((p) => p.code === e.code));
      setNcrProjects(projects);
      setNcrErrors(stillFailed);
      setNcrsLoadError(null);
      return;
    }
    setNcrProjects(projects);
    setNcrErrors(errors);
    // Every project failed (as opposed to a per-project partial failure,
    // which model.errors already surfaces inline): treat this like Today/
    // Plan/Weekly's own top-level failure so the screen shows an actionable
    // error instead of an empty board. sign_in_required takes priority
    // since it is the one actionable case (the others aren't user-fixable).
    if (projects.length === 0 && errors.length > 0) {
      const signIn = errors.find((e) => e.error === "sign_in_required");
      setNcrsLoadError(signIn ? signIn.error : errors[0].error);
    } else {
      setNcrsLoadError(null);
    }
  }

  useEffect(() => {
    if (!fixture && !signedIn) return;
    if (screen !== "ncrs" && ncrProjects) return; // load once, then only on nav/mutation, same as Plan
    let cancelled = false;
    (async () => {
      await loadNcrs();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixture, signedIn, screen, projectCodes.join(",")]);

  loadNcrsRef.current = loadNcrs;

  // NCR freshness: refetch on window focus and every 30 s, ONLY while the NCR
  // screen is showing and the tab is visible (startNcrPolling pauses on
  // document.visibilityState === "hidden"). Leaving the screen stops it, so
  // no other screen ever polls. Fixture mode is static and never polls.
  useEffect(() => {
    if (fixture || !signedIn || screen !== "ncrs") return;
    const poller = startNcrPolling({
      doc: typeof document !== "undefined" ? document : null,
      win: typeof window !== "undefined" ? window : null,
      refetch: () => loadNcrsRef.current({ quiet: true }),
    });
    return () => poller.stop();
  }, [fixture, signedIn, screen]);

  const ncrModel = useMemo(() => {
    if (!ncrProjects) return null;
    // Same project-filter rule as Today (filterEnvelopeByCodes above): the
    // top bar's pill governs every screen, so an NCR loaded for a
    // deselected project must not render here either.
    return shapeNcrScreen(filterByProjectCodes(ncrProjects, selectedCodes), filterByProjectCodes(ncrErrors, selectedCodes));
  }, [ncrProjects, ncrErrors, selectedCodes]);

  /**
   * Weekly report — `GET /api/cockpit/reports?weekStart=` re-read on every
   * navigation to the screen and after every mutating action (confirm /
   * approve / reopen / publish / regenerate), same read-back discipline as
   * Plan: this screen never advances state optimistically, it always shows
   * the server's own row after a write.
   */
  function showReport(env) {
    reportEnvelopeRef.current = env;
    setReportEnvelope(env);
  }

  async function loadReportPage(forWeek, { fresh = false, quiet = false } = {}) {
    // `forWeek` is the VIEW key: a Monday, or "start..end" for a custom period.
    const forPeriod = parsePeriodKey(forWeek);
    const cache = reportWeekCacheRef.current;
    if (fresh) cache.delete(forWeek); // a write just happened — never serve pre-write data
    const cached = cache.get(forWeek);
    const myGen = ++reportRequestRef.current; // only the latest request is ever applied
    if (cached && !fresh) {
      // Visited week: render at once, revalidate in the background.
      showReport(cached);
      setReportPageLoadError(null);
      setReportPageLoading(false);
    } else if (!quiet) {
      setReportPageLoading(true);
    }
    try {
      if (fixture) {
        const result = await fixtureReportStore.fetchReports(forPeriod ? undefined : forWeek, forPeriod || undefined);
        if (myGen !== reportRequestRef.current) return;
        if (result.ok) {
          showReport(result.body);
          setReportPageLoadError(null);
          setReportPeriodError(null);
        } else if (forPeriod && result.body && isPeriodErrorCode(result.body.errorCode)) {
          setReportPeriodError(result.body);
        }
        return;
      }
      const result = await api.fetchReports(forPeriod ? undefined : forWeek, forPeriod || undefined);
      if (myGen !== reportRequestRef.current) return;
      if (result.ok) cache.set(forWeek, result.body);
      if (!decideWeekCommit({ forWeek, shownWeek: reportViewKeyRef.current }).show) return; // cached only
      if (result.ok) {
        showReport(result.body);
        setReportPageLoadError(null);
        setReportPeriodError(null);
      } else if (forPeriod && result.body && isPeriodErrorCode(result.body.errorCode)) {
        // A refused period (invalid / too long / future / overlap) is a form
        // message next to the control, never the whole-screen error state.
        setReportPeriodError(result.body);
      } else if (!(cached && !fresh) && !quiet) {
        setReportPageLoadError(result.body && result.body.errorCode ? result.body.errorCode : `http_${result.status}`);
      }
    } finally {
      if (myGen === reportRequestRef.current) setReportPageLoading(false);
    }
  }

  // Prefetches the previous week once the current one has rendered, the same
  // way Plan prefetches its neighbours. Only fills the cache; never touches
  // what is on screen.
  useEffect(() => {
    if (fixture || !signedIn || screen !== "weekly" || !reportEnvelope || reportPageLoading || reportPeriod) return;
    const target = addWeeks(reportWeekStart, -1);
    const cache = reportWeekCacheRef.current;
    if (cache.has(target)) return;
    let cancelled = false;
    (async () => {
      const result = await api.fetchReports(target);
      if (cancelled || !result.ok || cache.has(target)) return;
      cache.set(target, result.body);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixture, signedIn, screen, reportWeekStart, reportEnvelope, reportPageLoading, reportPeriod]);

  useEffect(() => {
    if (!signedIn && !fixture) return;
    if (screen !== "weekly" && reportEnvelope) return; // load once, then only on nav/week-change/mutation
    let cancelled = false;
    (async () => {
      await loadReportPage(reportViewKey);
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixture, signedIn, screen, reportViewKey]);

  const reportModel = useMemo(() => {
    if (!reportEnvelope) return null;
    // `currentWeekStart` (above) is the one shared current-week source —
    // the header's "this week" comparison agrees with the Next-week clamp
    // and "Back to this week" handlers below, never a separate clock.
    return shapeReportPage(filterEnvelopeByCodes(reportEnvelope, selectedCodes), undefined, currentWeekStart);
  }, [reportEnvelope, selectedCodes, currentWeekStart]);

  /**
   * "Generate report" — the readinessHash sent is always the one the last
   * `GET /api/cockpit/reports` read carried (the hash the site manager
   * confirmed), never recomputed here; the server re-checks it. The call asks
   * for a queued job (`async: true`: 202 + jobId, because a generation takes
   * longer than the request timeout); the page is re-read at once so the
   * backend's `generationInProgress` shows, and the screen follows it from there.
   */
  async function handleReportGenerate(code, { prose, reason } = {}) {
    const project = reportModel.projects.find((p) => p.code === code);
    const body = buildReportConfirmBody({
      weekStart: project.weekStart,
      readinessHash: project.readinessHash,
      regenerateReason: reason || undefined,
      prose,
      period: reportPeriodRef.current,
      async: true,
    });
    const result = fixture ? await fixtureReportStore.postReportWeekly(code, body) : await api.postReportWeekly(code, body);
    await loadReportPage(reportViewKey, { fresh: true, quiet: true });
    return result.ok ? result.body : result.body || { errorCode: `http_${result.status}` };
  }

  /** Where a queued generation has got to (CisReportStatus); only read once the signal on the page has gone. */
  async function handleReportJobStatus(code, jobId) {
    const result = fixture ? await fixtureReportStore.fetchReportStatus(code, jobId) : await api.fetchReportStatus(code, jobId);
    return result.ok ? result.body : result.body || { errorCode: `http_${result.status}` };
  }

  async function reportPatch(code, reportNumber, action, etag, reason) {
    const body = buildReportPatchBody({ etag, action, reason });
    const result = fixture ? await fixtureReportStore.patchReportWeekly(code, reportNumber, body) : await api.patchReportWeekly(code, reportNumber, body);
    // The answer is the server's own read-back of the row: show it at once, so
    // the screen is right even if re-reading the page is refused (a period that
    // now overlaps an approved report answers 409 period_overlap).
    if (result.ok && result.body && reportEnvelopeRef.current) showReport(applyRowToEnvelope(reportEnvelopeRef.current, code, result.body));
    await loadReportPage(reportViewKey, { fresh: true });
    return result.ok ? result.body : result.body || { errorCode: `http_${result.status}` };
  }

  /**
   * SPEC-CIS-COCKPIT-READINESS-ACTIONS-001 §4.3/§4.4. Both act on one
   * inspection of the readiness "without photos" line, then reload the
   * report page so the line count and rows reflect the write. Fixture mode
   * has no backend: it reports that plainly instead of faking success.
   */
  /**
   * Applies a waiver/remind response's `readiness` to the report state at
   * once (this week's page AND Today's side card). Returns false when the
   * response carried none (or it does not match the loaded week), so the
   * caller falls back to a reload.
   */
  function applyReadinessFromResponse(code, weekStartForCall, readiness) {
    // Patch the CURRENT state (refs, not a render-time closure), and only the
    // view the response is for (a week's Monday, or a period's "start..end");
    // other views touch the cache entry alone.
    const out = applyReadinessUpdate({
      shown: reportEnvelopeRef.current,
      shownWeek: reportViewKeyRef.current,
      cache: reportWeekCacheRef.current,
      week: weekStartForCall,
      code,
      readiness,
    });
    if (!out.applied) return false;
    if (out.supersedeLoads) {
      // Older in-flight report loads must not overwrite the patch.
      reportRequestRef.current += 1;
      setReportPageLoading(false);
    }
    if (out.shown !== reportEnvelopeRef.current) showReport(out.shown);
    setReportsEnvelope((prev) => patchReportReadiness(prev, code, weekStartForCall, readiness) || prev);
    return true;
  }

  /** Fallback when a response carried no readiness: reload the week if it is still the one on screen. */
  async function reloadReportWeekAfterAction(week) {
    if (reportViewKeyRef.current === week) await loadReportPage(week, { fresh: true });
    else reportWeekCacheRef.current.delete(week);
  }

  // `period` is the custom period Report.jsx has on screen (null on a week):
  // the call carries it, and the returned readiness is applied to THAT view.
  async function handleRemindPhoto(code, inspectionNumber, period) {
    const view = period ? periodKey(period) : reportWeekStart;
    if (fixture) {
      if (fixture !== "report-period" && !reportV2) return { errorCode: "fixture_mode" };
      const sent = await fixtureReportStore.remindPhoto(code, inspectionNumber, reportWeekStart, period);
      const fixtureReadiness = sent.ok && sent.body ? sent.body.readiness : null;
      if (fixtureReadiness) applyReadinessFromResponse(code, view, fixtureReadiness);
      return sent.ok ? sent.body : sent.body || { errorCode: `http_${sent.status}` };
    }
    const week = view;
    const result = await api.remindPhoto(code, inspectionNumber, reportWeekStart, period || undefined);
    const readiness = result.ok && result.body ? result.body.readiness : null;
    if (!(readiness && applyReadinessFromResponse(code, week, readiness))) {
      await reloadReportWeekAfterAction(week);
    }
    return result.ok ? result.body : result.body || { errorCode: `http_${result.status}` };
  }

  async function handleWaivePhoto(code, inspectionNumber, etag, reason, period) {
    const view = period ? periodKey(period) : reportWeekStart;
    if (fixture) {
      if (fixture !== "report-period" && !reportV2) return { errorCode: "fixture_mode" };
      const waived = await fixtureReportStore.waivePhoto(code, inspectionNumber, etag, reason, reportWeekStart, period);
      const fixtureReadiness = waived.ok && waived.body ? waived.body.readiness : null;
      if (fixtureReadiness) applyReadinessFromResponse(code, view, fixtureReadiness);
      return waived.ok ? { status: "waived" } : waived.body || { errorCode: `http_${waived.status}` };
    }
    const week = view;
    const result = await api.waivePhoto(code, inspectionNumber, etag, reason, reportWeekStart, period || undefined);
    const readiness = result.ok && result.body ? result.body.readiness : null;
    if (!(readiness && applyReadinessFromResponse(code, week, readiness))) {
      await reloadReportWeekAfterAction(week);
    }
    return result.ok ? { status: "waived" } : result.body || { errorCode: `http_${result.status}` };
  }

  // "Custom period" (section 4.5): a valid pair reads readiness for that
  // period; "Use week" drops back to the week the screen was on.
  const handleSelectReportPeriod = (period) => {
    setReportPeriodError(null);
    setReportPeriod({ periodStart: period.periodStart, periodEnd: period.periodEnd });
  };
  const handleClearReportPeriod = () => {
    setReportPeriodError(null);
    setReportPeriod(null);
  };

  const handleReportApprove = (code, reportNumber, etag) => reportPatch(code, reportNumber, "approve", etag);
  const handleReportReopen = (code, reportNumber, etag, reason) => reportPatch(code, reportNumber, "reopen", etag, reason);
  const handleReportPublish = (code, reportNumber, etag) => reportPatch(code, reportNumber, "publish", etag);
  const handleReportRenderPdf = (code, reportNumber, etag) => reportPatch(code, reportNumber, "render_pdf", etag);
  // SPEC-CIS-REPORT-SCHEDULED-PUBLISH-001: schedule (Monday 09:00 site time) and hold.
  const handleReportSchedule = (code, reportNumber, etag) => reportPatch(code, reportNumber, "schedule", etag);
  const handleReportHold = (code, reportNumber, etag) => reportPatch(code, reportNumber, "hold", etag);

  /**
   * The preview pane never approximates — it calls the real, pure-read
   * `POST /api/cockpit/ncrs/raise?preview=1` route (CisCockpitNcrRaise's
   * preview branch, shared/raise.js previewProjectRaise) with exactly the
   * rows the screen wants previewed (the current selection, or just the
   * active row) and returns the per-project response verbatim, keyed by
   * project code. A fresh raiseId is generated per call: preview is a read,
   * never persisted, so there is no retry/idempotency reason to reuse one
   * (unlike the actual raise send, which does reuse its raiseId).
   */
  async function handleNcrPreview(rows, edits) {
    if (!rows.length) return {};
    const body = buildRaiseBody(rows, newRaiseId(), edits);
    const result = fixture ? await fixtureNcrStore.previewRaise(body) : await api.previewRaise(body);
    const byCode = {};
    if (result.ok && result.body && Array.isArray(result.body.projects)) {
      for (const project of result.body.projects) byCode[project.code] = project;
    }
    return byCode;
  }

  async function handleNcrSaveDescription(row, description) {
    ncrGuardRef.current.supersede(); // in-flight older NCR loads must not land over this write's reload
    const patch = buildDescriptionPatch(row, description);
    if (fixture) await fixtureNcrStore.patchNcr(row.code, row.ncrNumber, patch);
    else await api.patchNcr(row.code, row.ncrNumber, patch);
    await loadNcrs();
  }

  async function handleNcrReturn(row, note) {
    ncrGuardRef.current.supersede(); // in-flight older NCR loads must not land over this write's reload
    const patch = buildReturnPatch(row, note);
    if (fixture) await fixtureNcrStore.patchNcr(row.code, row.ncrNumber, patch);
    else await api.patchNcr(row.code, row.ncrNumber, patch);
    await loadNcrs();
  }

  /** SPEC-CIS-NCR-RETURN-RESUBMIT-001 §4.7/§5: one history GET for the NCR detail panel, used for display only. */
  async function handleNcrLoadHistory(row) {
    const result = fixture ? await fixtureNcrStore.fetchNcrReturns(row.code, row.ncrNumber) : await api.fetchNcrReturns(row.code, row.ncrNumber);
    return result.ok && Array.isArray(result.body) ? result.body : [];
  }

  async function handleNcrDismiss(row, note) {
    ncrGuardRef.current.supersede(); // in-flight older NCR loads must not land over this write's reload
    const patch = buildDismissPatch(row, note);
    if (fixture) await fixtureNcrStore.patchNcr(row.code, row.ncrNumber, patch);
    else await api.patchNcr(row.code, row.ncrNumber, patch);
    await loadNcrs();
  }

  async function handleNcrRaise(rows, raiseIdValue, edits) {
    ncrGuardRef.current.supersede();
    const body = buildRaiseBody(rows, raiseIdValue, edits);
    const result = fixture ? await fixtureNcrStore.raiseNcrs(body) : await api.raiseNcrs(body);
    await loadNcrs();
    return result.ok ? result.body : { raiseId: raiseIdValue, projects: rows.map((r) => ({ code: r.code, status: "failed", error: "transient_upstream" })), errors: [] };
  }

  async function handleNcrResolve(runId, outcome) {
    ncrGuardRef.current.supersede();
    const body = buildResolveBody(outcome);
    const result = fixture ? await fixtureNcrStore.resolveRaise(runId, body) : await api.resolveNcrRaise(runId, body);
    await loadNcrs();
    return result.ok ? result.body : { runId, status: "failed" };
  }

  /**
   * "Export NCR register" (EP-CIS-004, DEC-CHAT-20261010-404) — one call per
   * project through the existing CisNcrExport route; the screen itself fans
   * out per project (shape.js runNcrRegisterExports) and renders the
   * per-project outcome. A pure read of the register plus an archive write
   * server-side — never a row mutation, so no loadNcrs() reload and no
   * guard supersede. Live mode only: fixture mode (the artboard-conformance
   * twin) gets no exporter, so the control does not render there — same
   * fixture-gating Plan uses for onDetailSelect/onShowMoreBacklog.
   */
  const handleNcrExport = (code) => api.exportNcrs(code);

  // "To book" backlog paging/scoping (§4.3, PR #1958; codex review findings
  // App.jsx:183/724) — `computeBacklogPaging` (shape.js) is the one place
  // that reconciles the server's cross-project backlogTotal/paging with a
  // project filter; see its own doc comment.
  const backlogPaging = useMemo(
    () => computeBacklogPaging({
      rawBacklog: [...((planEnvelope && planEnvelope.backlog) || []), ...backlogExtra],
      rawTotal: planEnvelope && planEnvelope.backlogTotal,
      selectedCodes,
    }),
    [planEnvelope, backlogExtra, selectedCodes]
  );

  const planModel = useMemo(() => {
    if (!planEnvelope) return null;
    const today = fixture ? planWorld.today : new Date().toISOString().slice(0, 10);
    // Same project-filter rule as Today/NCR/Weekly report, extended to the
    // roster itself (owner live-use defect 2026-09-30, filterPlanLanes):
    // `lanes` is scoped to the selected project(s) too, never every sm
    // project's roster regardless of what "All projects" actually shows —
    // that is what put the SS411R CI service identity, the EPHA test
    // account and a garbled roster entry in front of the owner with no
    // project selected.
    const filteredPlanEnvelope = {
      ...planEnvelope,
      lanes: filterPlanLanes(planEnvelope.lanes, selectedCodes),
      items: filterByProjectCodes(planEnvelope.items, selectedCodes),
      unbooked: filterByProjectCodes(planEnvelope.unbooked, selectedCodes),
      backlog: backlogPaging.backlog,
      backlogTotal: backlogPaging.backlogTotal,
      imports: filterByProjectCodes(planEnvelope.imports, selectedCodes),
      forecast: filterPlanForecast(planEnvelope.forecast, selectedCodes, projectCodes),
      repairChecks: filterPlanRepairChecks(planEnvelope.repairChecks, selectedCodes),
      errors: filterByProjectCodes(planEnvelope.errors, selectedCodes),
    };
    return shapePlanScreen(filteredPlanEnvelope, today);
  }, [planEnvelope, fixture, selectedCodes, backlogPaging]);

  /**
   * "Show N more" in the To-book column. Loops fetching further raw server
   * pages (never stopping after a single page that happens to match
   * nothing) until either a page contributes at least one row the current
   * project filter keeps, or the server's own list is exhausted — a scoped
   * filter would otherwise frequently need several dead clicks in a row
   * before anything visibly changed (codex review finding, App.jsx:724).
   * A page that comes back with zero raw rows at all ends the loop
   * immediately; "Show more" never stays clickable past that point
   * (`backlogPaging.canShowMore` — recomputed from the fresh state next
   * render — governs whether the button renders at all).
   */
  async function handleShowMoreBacklog() {
    if (!planEnvelope || fixture) return; // the fixture's backlog is small enough to never page
    setBacklogLoadingMore(true);
    try {
      // The paging loop itself is `fetchBacklogPagesUntilMatch` (shape.js) —
      // a pure function with its own running total, never App.jsx's
      // `backlogExtra` closure (codex review round 2, P1: the previous
      // inline loop re-read that closure every iteration, which never
      // advances within one call, so the offset never advanced either and
      // the same page was fetched forever).
      const { extra } = await fetchBacklogPagesUntilMatch({
        fetchPage: async (offset) => {
          const result = await api.fetchPlan(weekStart, offset);
          return result.ok && result.body && Array.isArray(result.body.backlog) ? result.body.backlog : [];
        },
        pageOneLength: (planEnvelope.backlog || []).length,
        initialExtra: backlogExtra,
        isMatch: (row) => (selectedCodes.length ? selectedCodes.includes(row.code) : true),
      });
      setBacklogExtra(extra);
    } finally {
      setBacklogLoadingMore(false);
    }
  }

  /**
   * De-syncs `backlogExtra` (pages 2+) after any write that can change the
   * backlog — a drag/drop booking, a pinned re-inspection booking, or an
   * import commit (codex review finding, App.jsx:183): the write's own
   * `loadPlan(..., { isWrite: true })` already refreshes page 1 fresh, but
   * `backlogExtra`'s OLD pages can now duplicate rows page 1 just picked up,
   * or skip rows the write shifted off a page boundary. Resets to page 1,
   * then refetches fresh pages up to the same ROW COUNT that was loaded
   * before the write, de-duplicating by project + kind + ref against
   * whatever page 1 (the just-refreshed `planCacheRef` entry) now holds.
   */
  async function resyncBacklogAfterWrite(forWeek, priorRowCount) {
    setBacklogExtra([]);
    if (fixture) return;
    const page1 = planCacheRef.current.get(forWeek);
    const page1Backlog = (page1 && page1.backlog) || [];
    const page1Total = (page1 && typeof page1.backlogTotal === "number") ? page1.backlogTotal : page1Backlog.length;
    if (priorRowCount <= page1Backlog.length) return; // page 1 alone already covers what was loaded before
    const dedupeKeyOf = (row) => `${row.code}|${row.kind}|${row.scheduleRef || row.inspectionNumber}`;
    const seen = new Set(page1Backlog.map(dedupeKeyOf));
    const extra = [];
    let offset = page1Backlog.length;
    while (page1Backlog.length + extra.length < priorRowCount && offset < page1Total) {
      const result = await api.fetchPlan(forWeek, offset);
      const page = result.ok && result.body && Array.isArray(result.body.backlog) ? result.body.backlog : [];
      if (!page.length) break;
      for (const row of page) {
        const key = dedupeKeyOf(row);
        if (seen.has(key)) continue;
        seen.add(key);
        extra.push(row);
      }
      offset += page.length;
    }
    setBacklogExtra(extra);
  }

  async function runUpsert(code, items) {
    if (fixture) {
      window.__fixtureUpsertCalls = [...(window.__fixtureUpsertCalls || []), { code, items }];
      return fixturePlanStore.upsertSchedule(code, { items });
    }
    return api.upsertSchedule(code, items);
  }

  /**
   * A forecast item dropped on a lane cell (day redesign phase 2, 2.5): read the
   * stored row (the forward band carries only a few of its fields), then ONE
   * upsert of that row with only the date and the assignee changed and
   * `basis: ""`. The Plan is then re-read quietly; the booked item has left
   * the forward schedule and sits in the lane.
   */
  async function handlePlanBookForecast(item, { assignedTo, plannedDate }) {
    const label = item.label || item.scheduleRef;
    // One booking per forecast item at a time: a second confirm or drop while the first is in flight would send a
    // second upsert on the same etag and show a false conflict.
    const flightKey = `${item.code}:${item.scheduleRef}`;
    if (forecastBookingRef.current.has(flightKey)) return;
    forecastBookingRef.current.add(flightKey);
    setPlanSavingRef(item.scheduleRef);
    const week = weekStart;
    const priorBacklogRowCount = ((planEnvelopeRef.current && planEnvelopeRef.current.backlog) || []).length + backlogExtra.length;
    try {
      const read = fixture
        ? await fixturePlanStore.fetchScheduleRows(item.code, item.date, item.date)
        : await api.fetchScheduleRows(item.code, item.date, item.date);
      const stored = read.ok && Array.isArray(read.body) ? read.body.find((r) => r.scheduleRef === item.scheduleRef) : null;
      if (!stored) {
        setPlanNotice(`Could not book ${label}: the yard's row was not found. The Plan was refreshed.`);
        return;
      }
      const result = await runUpsert(item.code, [buildForecastBookRow(stored, { assignedTo, plannedDate })]);
      const res = result.ok && Array.isArray(result.body) ? result.body[0] : null;
      if (res && (res.status === "updated" || res.status === "duplicate")) {
        setPlanNotice(null);
      } else if (res && res.status === "invalid") {
        setPlanNotice(describePlanInvalid("book", item.scheduleRef, item.code, assignedTo, res.errors));
      } else if (res && res.status === "conflict") {
        setPlanNotice(`Could not book ${label}: it changed since this was read. The Plan was refreshed.`);
      } else {
        setPlanNotice(`Could not book ${label}: ${describePlanUpsertFailure(res, result, ["updated", "duplicate"])}.`);
      }
    } catch (err) {
      setPlanNotice(`Could not book ${label}: ${(err && err.message) || "request failed"}.`);
    } finally {
      forecastBookingRef.current.delete(flightKey);
      setPlanSavingRef(null);
      refreshPlanQuietly(week, priorBacklogRowCount, item.code);
    }
  }

  /** Approve, reopen or undo the repair link of the record in the detail panel; the panel is re-read afterwards. */
  async function handleReviewRecord(op, view, target, sheetIdentity) {
    const identity = sheetIdentity || planDetailIdentity;
    if (!identity) return { ok: false, message: "Select a record first." };
    const item = buildReviewItem(op, view, target);
    let result;
    if (fixture) {
      window.__fixtureReviewCalls = [...(window.__fixtureReviewCalls || []), { code: identity.code, items: [item] }];
      if (window.__fixtureReviewStale) {
        // Offline check: the record changed under the user. Nothing is applied, the row gets a new etag.
        window.__fixtureReviewStale = false;
        fixtureRegisterRef.current = fixtureRegisterRef.current.map((r) => (Number(r.inspectionNumber) === Number(item.inspectionNumber) ? { ...r, etag: `${r.etag.replace(/"$/, "")}-v2"` } : r));
        setFixtureRegister(fixtureRegisterRef.current);
        result = { ok: true, status: 200, body: [{ inspectionNumber: item.inspectionNumber, status: "stale_etag" }] };
      } else {
      fixtureRegisterRef.current = fixtureRegisterRef.current.map((r) => (Number(r.inspectionNumber) === Number(item.inspectionNumber) ? applyFixtureReview(r, item) : r));
      setFixtureRegister(fixtureRegisterRef.current);
      result = { ok: true, status: 200, body: [{ inspectionNumber: item.inspectionNumber, status: "ok" }] };
      }
    } else {
      result = await api.reviewRecords(identity.code, [item]);
    }
    const outcome = readReviewResult(result);
    const sameAsPanel = planDetailIdentity && planDetailIdentity.code === identity.code && Number(planDetailIdentity.inspectionNumber) === Number(identity.inspectionNumber);
    if (!fixture) {
      if (sameAsPanel) await loadPlanDetailExtras(identity, true);
      else invalidatePlanDetailCache(identity.code);
    }
    return outcome;
  }

  /**
   * Quiet background tail of every optimistic write: refetch the week (never
   * shows a loading state or an error screen) and re-sync the backlog pages.
   * Not awaited by the caller — the tile has already moved and "Saving…" has
   * already cleared.
   */
  function refreshPlanQuietly(forWeek, priorBacklogRowCount, code) {
    (async () => {
      try {
        await loadPlan(forWeek, { isWrite: true, quiet: true });
        await resyncBacklogAfterWrite(forWeek, priorBacklogRowCount);
      } catch {
        // Background only — the reconciled data stays on screen.
      }
      if (code) invalidatePlanDetailCache(code);
    })();
  }

  /**
   * A drop that changes assignedTo/plannedDate on an existing row (grid tile
   * or an undated item dragged onto the grid). The tile moves at once
   * (optimistic, with the existing "Saving…" marker); the upsert's own
   * read-back for THAT row then reconciles it — no whole-Plan refetch on the
   * critical path. A failure puts the tile back and shows the notice. The
   * commit gate / write epoch protections (createPlanOptimistic) are the same
   * ones the old refetch-after-write path relied on.
   */
  async function handlePlanDrop(tile, { assignedTo, plannedDate }) {
    setPlanSavingRef(tile.scheduleRef);
    const week = weekStart;
    const priorBacklogRowCount = ((planEnvelopeRef.current && planEnvelopeRef.current.backlog) || []).length + backlogExtra.length;
    const base = planEnvelopeRef.current;
    const txn = base
      ? planOptimistic.begin(week, { code: tile.code, scheduleRef: tile.scheduleRef, apply: (env) => applyOptimisticMove(env, tile, { assignedTo, plannedDate }) })
      : null;
    // A quiet week refetch ALWAYS follows, success or failure, so whatever
    // the optimistic bookkeeping left is healed from server data.
    const refetch = true;
    try {
      const row = buildDragUpsertRow(tile, { assignedTo, plannedDate });
      const result = await runUpsert(tile.code, [row]);
      const item = result.ok && Array.isArray(result.body) ? result.body[0] : null;
      if (item && (item.status === "created" || item.status === "updated")) {
        setPlanNotice(null);
        if (txn) planOptimistic.confirm(txn, (env) => reconcileUpsertReadback(env, tile.code, tile.scheduleRef, item.readback));
      } else {
        if (txn) planOptimistic.revert(txn);
        if (item && item.status === "invalid") {
          setPlanNotice(describePlanInvalid("move", tile.scheduleRef, tile.code, assignedTo, item.errors));
        } else if (item && item.status === "conflict") {
          setPlanNotice(`Could not move ${tile.scheduleRef}: it changed since this was read. Refreshed.`);
        } else {
          // A failed request (!item), or an HTTP 200 whose item is itself a
          // failure (status "error" + errorCode), is never a silent success.
          const reason = describePlanUpsertFailure(item, result, ["created", "updated"]);
          setPlanNotice(`Could not move ${tile.scheduleRef}: ${reason}.`);
        }
      }
    } catch (err) {
      if (txn) planOptimistic.revert(txn);
      setPlanNotice(`Could not move ${tile.scheduleRef}: ${(err && err.message) || "request failed"}.`);
    } finally {
      setPlanSavingRef(null);
    }
    if (refetch) refreshPlanQuietly(week, priorBacklogRowCount, tile.code);
  }

  /**
   * "+ Schedule" form save: the existing upsert (POST /api/projects/{code}/
   * schedule) with one new row. The item shows on the grid at once; on any
   * failure it is removed again and `{ ok:false, message }` goes back to the
   * form, which shows it inline. Resolves `{ ok:true, outsideWeek }` on success.
   */
  async function handleScheduleCreate(code, item) {
    const week = weekStart;
    const priorBacklogRowCount = ((planEnvelopeRef.current && planEnvelopeRef.current.backlog) || []).length + backlogExtra.length;
    const base = planEnvelopeRef.current;
    const inWeek = item.plannedDate >= week && item.plannedDate <= addDays(week, 6);
    const txn = base
      ? planOptimistic.begin(week, { code, scheduleRef: item.scheduleRef, apply: (env) => applyOptimisticCreate(env, code, item) })
      : null;
    setPlanSavingRef(item.scheduleRef);
    try {
      const result = await runUpsert(code, [item]);
      const res = result.ok && Array.isArray(result.body) ? result.body[0] : null;
      if (res && (res.status === "created" || res.status === "duplicate")) {
        if (txn) planOptimistic.confirm(txn, (env) => reconcileUpsertReadback(env, code, item.scheduleRef, res.readback));
        refreshPlanQuietly(week, priorBacklogRowCount, code);
        setPlanNotice(inWeek ? null : `Scheduled ${item.title} for ${item.plannedDate} — that day is outside the week shown.`);
        return { ok: true, outsideWeek: !inWeek };
      }
      if (txn) planOptimistic.revert(txn);
      refreshPlanQuietly(week, priorBacklogRowCount, code);
      let message;
      if (res && res.status === "invalid") {
        message = (res.errors || []).map((e) => (Array.isArray(e) ? `${e[0]}: ${e[1]}` : String(e))).join("; ") || "The server refused this item";
      } else {
        message = describePlanUpsertFailure(res, result, ["created"]);
      }
      return { ok: false, message };
    } catch (err) {
      if (txn) planOptimistic.revert(txn);
      refreshPlanQuietly(week, priorBacklogRowCount, code);
      return { ok: false, message: (err && err.message) || "request failed" };
    } finally {
      setPlanSavingRef(null);
    }
  }

  /** A drop of the pinned overdue re-inspection onto the grid — books a new row (§4.3 booking rule). */
  async function handlePlanDropPinned(pinned, { assignedTo, plannedDate }) {
    setPlanSavingRef(pinned.scheduleRef || pinned.inspectionNumber);
    try {
      const row = buildBookingRow(pinned, { assignedTo, plannedDate });
      const result = await runUpsert(pinned.code, [row]);
      const item = result.ok && Array.isArray(result.body) ? result.body[0] : null;
      if (item && item.status === "invalid") {
        setPlanNotice(describePlanInvalid("book", item.scheduleRef || pinned.code, pinned.code, assignedTo, item.errors));
      } else if (item && item.status === "duplicate") {
        setPlanNotice(`${pinned.code} booking already exists — nothing new created.`);
        setPinnedBooking(null);
      } else if (item && item.status === "created") {
        setPlanNotice(null);
        setPinnedBooking(null);
      } else {
        // Codex review finding (PR #1963 P1, then P2 round 2): same
        // silent-failure gap as handlePlanDrop above — a failed request,
        // or an HTTP 200 whose item itself is a failure (status "error",
        // with its own errorCode), must never be indistinguishable from a
        // successful booking.
        const reason = describePlanUpsertFailure(item, result, ["created"]);
        setPlanNotice(`Could not book ${pinned.code}: ${reason}.`);
      }
      const priorBacklogRowCount = ((planEnvelope && planEnvelope.backlog) || []).length + backlogExtra.length;
      await loadPlan(weekStart, { isWrite: true });
      await resyncBacklogAfterWrite(weekStart, priorBacklogRowCount);
      invalidatePlanDetailCache(pinned.code);
    } finally {
      setPlanSavingRef(null);
    }
  }

  // Yard schedule import (SPEC-CIS-SITE-MANAGER-COCKPIT-V3-001 §4.3,
  // DEC-CHAT-20260930-204): single-project only — "All projects" (no code,
  // or every code, per TopBar's own `allSelected`) has no one `{code}` for
  // the preview/commit routes, so the button is refused client-side via the
  // same `planNotice` banner drag-and-drop already renders, never a file
  // dialog or a request.
  const singleProjectCode = singleSelectedProjectCode(selectedCodes) || (projectCodes.length === 1 ? projectCodes[0] : null);

  // Owner retest 4: the project's recent import runs (file name, date, first
  // planned date) so Plan can label rows "Imported from <file> on <date>" and
  // offer "From import". Best effort: a failed read just leaves labels
  // date-only (the schedule row itself carries its own import time).
  const [importRuns, setImportRuns] = useState([]);
  const importPhase = importPanel && importPanel.phase;
  useEffect(() => {
    if (fixture) {
      setImportRuns(fixtureImportRuns);
      return undefined;
    }
    if (screen !== "plan" || !signedIn || !singleProjectCode) {
      setImportRuns([]);
      return undefined;
    }
    let cancelled = false;
    api.fetchImportHistory(singleProjectCode, 1).then((result) => {
      if (!cancelled && result && result.ok && result.body && Array.isArray(result.body.runs)) setImportRuns(result.body.runs);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixture, screen, signedIn, singleProjectCode, importPhase === "committed"]);

  function handleImportRefused() {
    setPlanNotice(IMPORT_REFUSED_MESSAGE);
    setDayNotice(IMPORT_REFUSED_MESSAGE);
  }

  const importGenRef = useRef(0);
  async function handleImportFileChosen(file) {
    const myImportGen = ++importGenRef.current;
    if (!singleProjectCode) {
      handleImportRefused();
      return;
    }
    setPlanNotice(null);
    setDayNotice(null);
    if (file && file.size === 0) {
      setImportPanel({ phase: "error", message: "The file is empty. Save it again from WeChat and choose it once more." });
      return;
    }
    setImportPanel({ phase: "uploading" });
    const result = fixture
      ? await fixturePlanStore.previewScheduleImport(singleProjectCode, file)
      : await api.previewScheduleImport(singleProjectCode, file);
    if (myImportGen !== importGenRef.current) return; // a newer file was chosen, or the panel was dismissed
    if (!result.ok || !result.body || !result.body.runId) {
      setImportPanel({ phase: "error", message: describeImportRefusal(result, singleProjectCode) });
      return;
    }
    // Owner row-level selection (6 Oct 2026 feedback: a 786-row preview with
    // one Apply committed the whole project). `candidates` is the server's
    // additive, bounded row list (shared/schedule-import.js buildCandidates);
    // default selection = new/changed rows that are owner inspections, and
    // "owner inspections only" defaults on wherever the format can tell —
    // the ITP ledger cannot (candidates.ownerInspectionField stays null),
    // so the filter has no effect there and is shown disabled.
    const candidates = shapeImportCandidates(result.body.preview);
    setImportPanel({
      phase: "preview",
      code: singleProjectCode,
      fileName: file.name || "",
      runId: result.body.runId,
      counts: shapeImportCounts(result.body.preview),
      candidates: candidates.rows,
      candidatesTotal: candidates.total,
      candidatesTruncated: candidates.truncated,
      ownerInspectionField: candidates.ownerInspectionField,
      ownerOnly: true,
      selectedKeys: new Set(defaultImportSelection(candidates.rows)),
    });
  }

  function handleImportToggleRow(key) {
    setImportPanel((p) => {
      if (!p || !p.selectedKeys) return p;
      const next = new Set(p.selectedKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...p, selectedKeys: next };
    });
  }

  function handleImportToggleOwnerOnly() {
    setImportPanel((p) => (p ? { ...p, ownerOnly: !p.ownerOnly } : p));
  }

  function handleImportSelectAllShown() {
    setImportPanel((p) => {
      if (!p || !p.candidates) return p;
      const visible = p.ownerOnly ? p.candidates.filter((r) => r.ownerInspection !== false) : p.candidates;
      // A cancel needs its own explicit tick; "select all" never includes one.
      return { ...p, selectedKeys: new Set(visible.filter((r) => r.kind !== "cancel").map((r) => r.key)) };
    });
  }

  function handleImportClearSelection() {
    setImportPanel((p) => (p ? { ...p, selectedKeys: new Set() } : p));
  }

  // Commit is budgeted per call server-side (schedule-import.js
  // commitImport's ops cap): a large import answers 200 committed_partial
  // with writes still outstanding, and the endpoint is idempotent on
  // re-commit by design — this loops the same way the legacy cockpit does
  // (cis-capture/app/js/cockpit.js:975-1020), reporting "Applied" only when
  // the server answers status "committed" with zero remaining and zero
  // errors, never on the first partial response.

  async function handleImportCommit() {
    if (!importPanel || !importPanel.runId || importPanel.code !== singleProjectCode) return;
    const { code, runId } = importPanel;
    // `selectedKeys` is the owner's row-level selection from the preview
    // list. `undefined` (no `candidates`/`selectedKeys` on this importPanel
    // at all — never happens from handleImportFileChosen above, but kept
    // for forward compatibility with any other producer of this state)
    // keeps today's whole-file commit; otherwise this is the exact set the
    // SM checked, sent on every retry pass so a committed_partial
    // continuation narrows to the SAME rows throughout.
    const selectedKeys = importPanel.selectedKeys ? Array.from(importPanel.selectedKeys) : undefined;
    // Captured once, before any pass — an import commit can add/remove far
    // more backlog rows than a single drag/booking, but the resync target
    // is still "however many rows were loaded before this write started"
    // (codex review finding, App.jsx:183).
    const priorBacklogRowCount = ((planEnvelope && planEnvelope.backlog) || []).length + backlogExtra.length;
    setImportPanel((p) => ({ ...p, phase: "committing", progress: null }));
    // The loop, its retry-once-on-timeout and its progress live in
    // api/importCommit.js (unit-tested); a pass is ~30 server writes.
    const outcome = await runImportCommit({
      commitPass: () => {
        const body = selectedKeys === undefined ? {} : { selectedKeys };
        return fixture
          ? fixturePlanStore.commitScheduleImport(code, runId, body)
          : api.commitScheduleImport(code, runId, body);
      },
      onProgress: ({ done, total }) => setImportPanel((p) => (p ? { ...p, progress: { done, total } } : p)),
    });
    if (outcome.outcome === "committed") {
      setImportPanel({ phase: "committed", code, runId, counts: shapeImportCounts(outcome.counts || (outcome.body && outcome.body.counts)) });
    } else {
      setImportPanel({ phase: "error", message: outcome.message });
    }
    await loadPlan(weekStart, { isWrite: true });
    await resyncBacklogAfterWrite(weekStart, priorBacklogRowCount);
    invalidatePlanDetailCache(code);
    // The daily list's rows land on Today unassigned: re-read it.
    if (!fixture) await loadToday({ quiet: true });
  }

  function handleImportDismiss() {
    importGenRef.current += 1;
    setImportPanel(null);
  }

  // --- Today by the yard's day (Day redesign phase 1) ---------------------
  const [dayNotice, setDayNotice] = useState(null);
  const [dayAssigned, setDayAssigned] = useState({});
  const [dayAdded, setDayAdded] = useState([]);
  const meUpn = fixture ? FIXTURE_DAY_ME : (auth.getUpn() || "").toLowerCase();

  /** One line to a person ("" = back to unassigned) through the existing schedule upsert. Resolves { ok, message }. */
  async function handleDayAssign(line, assignedTo) {
    const built = buildDayAssignItems(line, assignedTo);
    if (!built.items.length) return { ok: true };
    if (!built.ok && !fixture) return { ok: false, message: "Could not assign: this line has no version stamp (etag). Refresh Today and try again." };
    const items = built.items;
    const refs = items.map((i) => i.scheduleRef);
    const patch = Object.fromEntries(refs.map((r) => [r, assignedTo]));
    setDayAssigned((o) => ({ ...o, ...patch }));
    const clear = () => setDayAssigned((o) => { const next = { ...o }; for (const r of refs) delete next[r]; return next; });
    if (fixture) return { ok: true };
    const OK = ["created", "updated", "duplicate"];
    try {
      const result = await runUpsert(line.projectCode, items);
      const results = result.ok && Array.isArray(result.body) ? result.body : [];
      const pairs = items.map((it, i) => [it, results[i]]);
      const failed = pairs.filter(([, r]) => !r || !OK.includes(r.status));
      if (failed.length) {
        const [it, r] = failed[0];
        // A partial two-side failure, or a conflict, means the server holds something else now: read it back.
        if (failed.length < items.length || (r && r.status === "conflict")) await loadToday({ quiet: true });
        clear();
        if (r && r.status === "invalid") return { ok: false, message: describePlanInvalid("assign", it.scheduleRef, line.projectCode, assignedTo, r.errors) };
        if (r && r.status === "conflict") return { ok: false, message: "This line changed since it was read. Refreshed." };
        return { ok: false, message: `Could not assign: ${describePlanUpsertFailure(r, result, OK)}` };
      }
      await loadToday({ quiet: true });
      clear();
      return { ok: true };
    } catch (err) {
      clear();
      return { ok: false, message: (err && err.message) || "request failed" };
    }
  }

  /** "Add one inspection": the existing schedule upsert with the new optional fields (one item, or P and S). Resolves { ok, message }. */
  async function handleAddInspection(code, items) {
    if (fixture) {
      setDayAdded((a) => [...a, { code, items }]);
      return { ok: true };
    }
    const OK = ["created", "duplicate"];
    try {
      const result = await runUpsert(code, items);
      const results = result.ok && Array.isArray(result.body) ? result.body : [];
      const failed = items.map((it, i) => [it, results[i]]).filter(([, r]) => !r || !OK.includes(r.status));
      if (!failed.length) {
        await loadToday({ quiet: true });
        return { ok: true };
      }
      if (failed.length < items.length || (failed[0][1] && failed[0][1].status === "conflict")) await loadToday({ quiet: true });
      const r = failed[0][1];
      if (r && r.status === "invalid") {
        return { ok: false, message: (r.errors || []).map((e) => (Array.isArray(e) ? `${e[0]}: ${e[1]}` : String(e))).join("; ") || "The server refused this item" };
      }
      return { ok: false, message: describePlanUpsertFailure(r, result, OK) };
    } catch (err) {
      return { ok: false, message: (err && err.message) || "request failed" };
    }
  }

  // --- End of day (Day redesign phase 2, SPEC-CIS-COCKPIT-DAY-REDESIGN-P2-001 2.1-2.3) ---
  // Fixture mode records every write in window.__fixtureEodCalls and answers as the server
  // would; `window.__fixtureEodStale` / `__fixtureEodConflict` make the next write answer
  // stale_etag / conflict once (browser tests only).
  function fixtureEodCall(entry) {
    window.__fixtureEodCalls = [...(window.__fixtureEodCalls || []), entry];
  }

  async function eodReview(code, items) {
    if (!fixture) return api.reviewRecords(code, items);
    fixtureEodCall({ call: "review", code, items });
    if (window.__fixtureEodStale) {
      window.__fixtureEodStale = false;
      return { ok: true, status: 200, body: items.map((it) => ({ inspectionNumber: it.inspectionNumber, status: "stale_etag" })) };
    }
    setFixtureWritten((prev) => applyEndOfDayWrite(prev || fixtureTodayEnvelope, { type: "approve", projectCode: code, inspectionNumbers: items.map((it) => it.inspectionNumber) }));
    return { ok: true, status: 200, body: items.map((it) => ({ inspectionNumber: it.inspectionNumber, status: "ok" })) };
  }

  async function eodUpsert(code, items) {
    if (!fixture) return runUpsert(code, items);
    fixtureEodCall({ call: "upsert", code, items });
    if (window.__fixtureEodConflict) {
      window.__fixtureEodConflict = false;
      return { ok: true, status: 200, body: items.map((it) => ({ scheduleRef: it.scheduleRef, status: "conflict", errorCode: "already_recorded" })) };
    }
    const carriedTo = nextWorkingDay(eodModel ? eodModel.date : "");
    for (const it of items) {
      setFixtureWritten((prev) => applyEndOfDayWrite(prev || fixtureTodayEnvelope, { type: "close", projectCode: code, scheduleRef: it.scheduleRef, close: it.close, reason: it.reason, carriedTo }));
    }
    return { ok: true, status: 200, body: items.map((it) => ({ scheduleRef: it.scheduleRef, status: "ok" })) };
  }

  /** Carry or mark one side now, then re-read the day (fresh etags, whatever the outcome). */
  async function handleEodCloseSide(side, close, reason) {
    const outcome = await closeSide({ side, close, reason, upsert: eodUpsert });
    if (!fixture) await loadToday({ quiet: true, date: eodModel ? eodModel.date : undefined });
    return outcome;
  }

  /** The one submit: approve the ticked records, carry the postponed sides still set; the day is re-read afterwards. */
  async function handleEodSubmit({ records, carries }) {
    const outcome = await submitEndOfDay({ records, carries, review: eodReview, upsert: eodUpsert });
    if (!fixture) await loadToday({ quiet: true, date: eodModel ? eodModel.date : undefined });
    return outcome;
  }

  // Today follows the PROJECT's calendar day: the payload carries no time zone,
  // so it re-reads (and the server's day.date wins) every 10 minutes while the
  // tab is visible, on focus, and at once when the shown day is long gone
  // (todayRefreshDue, unit-tested). A UTC date key would refresh a UTC+8 yard
  // eight hours late.
  const shownDayRef = useRef("");
  useEffect(() => {
    if (fixture || !signedIn) return undefined;
    function check() {
      const visible = typeof document === "undefined" || document.visibilityState !== "hidden";
      if (todayRefreshDue({ shownDate: shownDayRef.current, lastReadAtMs: todayReadAtRef.current, nowMs: Date.now(), visible })) {
        todayReadAtRef.current = Date.now(); // one attempt per window, even if it fails
        loadToday({ quiet: true, background: true });
      }
    }
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    const id = setInterval(check, 60000);
    return () => {
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", check);
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixture, signedIn]);

  const reportsScreenEnvelope = fixture ? fixtureReportsEnvelope : reportsEnvelope;

  // Today's shaped model AND the nav badges come from the same filtered
  // envelope + the same shapeTodayScreen call, in one memo, so the two can
  // never disagree (the root cause of the live "994 items … Today 993" vs.
  // 4 rows shown defect: the lead text and the nav count were each computed
  // from a different, unfiltered source).
  const { todayModel, navCounts } = useMemo(() => {
    if (!envelope) return { todayModel: null, navCounts: {} };
    const filteredTodayEnvelope = filterEnvelopeByCodes(envelope, selectedCodes);
    const shapedToday = shapeTodayScreen(filteredTodayEnvelope);
    let reportsScreen = null;
    if (reportsScreenEnvelope) reportsScreen = shapeReportsScreen(filterEnvelopeByCodes(reportsScreenEnvelope, selectedCodes));
    else if (!fixture && reportsLoadError) reportsScreen = shapeReportsScreen(null, { failed: true, errorCode: reportsLoadError });
    // Today renders from its own data as soon as it arrives; the reports side
    // card loads separately and shows a small "loading" state meanwhile.
    const readinessLoading = !fixture && !reportsScreenEnvelope && !reportsLoadError;
    // Today now shows the yard's day, not the alert groups: its nav badge is dropped.
    return { todayModel: toTodayViewModel(shapedToday, reportsScreen, { readinessLoading }), navCounts: { ...shapedToday.counts, today: 0 } };
  }, [envelope, reportsScreenEnvelope, fixture, reportsLoadError, selectedCodes]);

  const dayModel = useMemo(() => {
    if (!envelope) return null;
    const withOverrides = applyDayOverrides(filterEnvelopeByCodes(envelope, selectedCodes), { assigned: dayAssigned, added: dayAdded });
    return shapeDayScreen(withOverrides, { me: meUpn });
  }, [envelope, selectedCodes, dayAssigned, dayAdded, meUpn]);
  shownDayRef.current = dayModel ? dayModel.date : "";

  const eodModel = useMemo(() => {
    if (!envelope) return null;
    return shapeEndOfDay(filterEnvelopeByCodes(envelope, selectedCodes), { me: meUpn });
  }, [envelope, selectedCodes, meUpn]);

  // Opening End of day reads the day afresh, so the etags it sends are current.
  useEffect(() => {
    if (fixture || !signedIn || screen !== "endofday") return;
    loadToday({ quiet: true, date: shownDayRef.current || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixture, signedIn, screen === "endofday"]);

  const errors = filterByProjectCodes((envelope && envelope.errors) || [], selectedCodes);
  // On Today the top bar names the SERVER's day (the project's own calendar day), not the browser's; while it loads it shows nothing (no flip from the browser week).
  const weekLabel = fixture ? fixtureMainMeta.weekLabel : ((screen === "today" || screen === "endofday") ? (dayModel && dayModel.date ? topBarDayLabel(dayModel.date) : "") : weekLabelNow());

  // Report's `inspection_photos` readiness line has no working "Remind
  // inspector" action (codex review round 2, App.jsx:1284): this app's only
  // reminder mechanism, `POST /api/cockpit/remind`, always sends the fixed
  // subject "Inspection result missing" — wrong for a missing-photo
  // reminder. A round-1 attempt resolved a scheduleRef from Plan's own
  // loaded rows and called that same endpoint when found; removed, along
  // with the resolver wiring below — `findScheduleRefForInspectionNumber`
  // (shape.js) stays, unused for now, for whenever a correct photo-
  // reminder route exists (needs its own spec, same as "Accept without
  // photo" — SPEC RULE: a new send mechanism).

  async function handleRemind(row) {
    const result = await api.remind(row.code, row.scheduleRef);
    if (result.ok && result.body && result.body.status === "sent") return { ok: true, message: "Reminder sent" };
    if (result.ok && result.body && result.body.status === "duplicate") return { ok: true, message: "Already reminded today" };
    const errorCode = (result.body && (result.body.errorCode || result.body.status)) || `http_${result.status}`;
    return { ok: false, message: errorCode };
  }

  // Report "Results missing" rows: title/date/inspector/trade come from the
  // Today data the app already holds for the same scheduleRef.
  function resolveMissingResult(code, scheduleRef) {
    const rows = (todayModel && todayModel.groups && todayModel.groups.resultsMissing) || [];
    return rows.find((r) => r.code === code && r.scheduleRef === scheduleRef) || null;
  }

  if (!fixture && !signedIn) {
    if (phone && initReady && !initError) {
      return (
        <div className="sm-root ph-root ph-bare" data-theme={theme} data-app-ready="true">
          <PhoneSignIn firstTime onSignIn={() => auth.login()} />
        </div>
      );
    }
    return (
      <div className={`sm-root${phone ? " ph-root ph-bare" : ""}`} data-theme={theme} data-app-ready={initReady ? "true" : "false"}>
        {!initReady ? (
          <div className="placeholder" aria-busy="true" />
        ) : initError ? (
          <ErrorState
            label="Sign-in"
            errorCode={initError}
            onSignIn={() => auth.login()}
            onRetry={() => {
              auth.resetInit("retry");
              setInitError(null);
              setInitAttempt((n) => n + 1);
            }}
          />
        ) : (
          <div className="placeholder">
            <button type="button" className="btn acc" onClick={() => auth.login()}>Sign in</button>
          </div>
        )}
      </div>
    );
  }

  // `?fixture=report-flow`: the approve -> PDF -> publish flow, panel by panel,
  // as Approve.dc.html draws it (artboard gate only; not reachable from the app).
  if (fixture === "report-flow") {
    return (
      <div className="sm-root" data-app-ready="true">
        <FlowGallery />
      </div>
    );
  }

  // `?fixture=signin`: the session-ended screen (artboard gate only; not reachable from the app).
  if (fixture === "signin") {
    return (
      <div className={`sm-root${phone ? " ph-root ph-bare" : ""}`} data-theme={theme} data-app-ready="true">
        <ErrorState label="Today" errorCode="sign_in_required" onSignIn={async () => undefined} onRetry={() => {}} />
      </div>
    );
  }

  return (
    <div className={`sm-root${phone ? " ph-root" : ""}`} data-app-ready="true">
      {phone ? null : (
        <TopBar
          screen={screen === "endofday" ? "today" : screen}
          onNavigate={handleNavigate}
          projectCodes={projectCodes}
          selectedCodes={selectedCodes}
          onSelectCodes={setSelectedCodes}
          navCounts={navCounts}
          weekLabel={weekLabel}
          identity={identity}
        />
      )}
      <PhoneShellProvider
        value={
          phone
            ? { screen: screen === "endofday" ? "today" : screen, onNavigate: handleNavigate, projectCodes, selectedCodes, onSelectCodes: setSelectedCodes, navCounts, weekLabel, identity }
            : null
        }
      >
      {screen === "today" ? (
        loadError ? (
          <ErrorState label="Today" errorCode={loadError} onSignIn={() => auth.login()} onRetry={loadToday} />
        ) : todayModel ? (
          <Today
            day={dayModel}
            errors={errors}
            notice={dayNotice}
            lanes={planModel ? planModel.lanes : []}
            me={meUpn}
            projectCodes={projectCodes}
            onNavigate={handleNavigate}
            onAssign={handleDayAssign}
            onAdd={handleAddInspection}
            canImport={Boolean(singleProjectCode)}
            importPanel={importPanel}
            onImportRefused={handleImportRefused}
            onImportFileChosen={handleImportFileChosen}
            onImportCommit={handleImportCommit}
            onImportDismiss={handleImportDismiss}
            onImportToggleRow={handleImportToggleRow}
            onImportToggleOwnerOnly={handleImportToggleOwnerOnly}
            onImportSelectAllShown={handleImportSelectAllShown}
            onImportClearSelection={handleImportClearSelection}
          />
        ) : (
          <div className="placeholder">Loading…</div>
        )
      ) : null}
      {screen === "endofday" ? (
        loadError ? (
          <ErrorState label="End of day" errorCode={loadError} onSignIn={() => auth.login()} onRetry={() => loadToday({ date: shownDayRef.current || undefined })} />
        ) : eodModel ? (
          <EndOfDay
            model={eodModel}
            errors={errors}
            onBack={() => handleNavigate("today")}
            onCloseSide={handleEodCloseSide}
            onSubmit={handleEodSubmit}
          />
        ) : (
          <div className="placeholder">Loading…</div>
        )
      ) : null}
      {screen === "plan" ? (
        planLoadError ? (
          // Week controls stay reachable even in the error state (codex
          // review finding, PR #1957, MEDIUM): a bad week (now normalised/
          // dropped by pageState.js's own readPageHash before this is ever
          // reached, but a legitimately-rejected week — e.g. too far in the
          // past/future — can still land here) must never leave the SM
          // stuck on Retry-only, resending the exact same week forever.
          <div className="page" style={{ flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn off" onClick={() => setWeekStart((w) => addWeeks(w, -1))}>
                Previous week
              </button>
              <button type="button" className="btn off" onClick={() => setWeekStart((w) => addWeeks(w, 1))}>
                Next week
              </button>
            </div>
            <ErrorState label="Plan" errorCode={planLoadError} onSignIn={() => auth.login()} onRetry={() => loadPlan(weekStart)} />
          </div>
        ) : planModel ? (
          <Plan
            model={planModel}
            pinnedBooking={pinnedBooking}
            focusTarget={planFocus}
            onFocusConsumed={() => setPlanFocus(null)}
            notice={planNotice}
            onDrop={handlePlanDrop}
            onDropPinned={handlePlanDropPinned}
            onPrevWeek={() => setWeekStart((w) => addWeeks(w, -1))}
            onNextWeek={() => setWeekStart((w) => addWeeks(w, 1))}
            weekLoading={planLoadingWeek}
            savingRef={planSavingRef}
            stale={planStaleWeek === weekStart}
            onRetryStale={() => loadPlan(weekStart)}
            scheduleProjects={selectedCodes.length ? selectedCodes : projectCodes}
            onScheduleCreate={handleScheduleCreate}
            canImport={Boolean(singleProjectCode)}
            canViewImportHistory={Boolean(singleProjectCode) || fixture}
            importHistoryScope={singleProjectCode}
            onLoadImportHistory={(page) => (fixture ? Promise.resolve({ ok: true, body: { runs: fixtureImportRuns, page: 1, hasMore: false } }) : api.fetchImportHistory(singleProjectCode, page))}
            importRuns={importRuns}
            onGoToWeek={(monday) => setWeekStart(monday)}
            importPanel={importPanel}
            onImportRefused={handleImportRefused}
            onImportFileChosen={handleImportFileChosen}
            onImportCommit={handleImportCommit}
            onImportDismiss={handleImportDismiss}
            onImportToggleRow={handleImportToggleRow}
            onImportToggleOwnerOnly={handleImportToggleOwnerOnly}
            onImportSelectAllShown={handleImportSelectAllShown}
            onImportClearSelection={handleImportClearSelection}
            backlogLoadingMore={backlogLoadingMore}
            onShowMoreBacklog={fixture ? undefined : handleShowMoreBacklog}
            canShowMoreBacklog={fixture ? false : backlogPaging.canShowMore}
            onDetailSelect={fixture && !forecastFixture ? undefined : setPlanDetailIdentity}
            detailExtras={fixture && !forecastFixture ? null : planDetailExtras}
            onOpenNcr={(payload) => handleNavigate("ncrs", payload)}
            onRetryDetail={fixture ? undefined : () => planDetailIdentity && loadPlanDetailExtras(planDetailIdentity, true)}
            onBookForecast={handlePlanBookForecast}
            onReviewRecord={handleReviewRecord}
            onLoadRecord={stableReadRecord}
            onLoadRecordPhoto={stableReadRecordPhoto}
            onReleaseRecord={stableReleaseRecord}
          />
        ) : (
          <div className="placeholder">Loading…</div>
        )
      ) : null}
      {screen === "ncrs" ? (
        ncrsLoadError ? (
          <ErrorState label="NCRs" errorCode={ncrsLoadError} onSignIn={() => auth.login()} onRetry={loadNcrs} />
        ) : ncrModel ? (
          <NCR
            model={ncrModel}
            initialSelectedKeys={fixture ? ncrModel.tabs.toDecide.slice(0, 2).map((r) => r.key) : []}
            initialActiveKey={selectedNcrKey}
            onSaveDescription={handleNcrSaveDescription}
            onReturn={handleNcrReturn}
            onDismiss={handleNcrDismiss}
            onRaise={handleNcrRaise}
            onResolve={handleNcrResolve}
            onPreview={handleNcrPreview}
            onLoadHistory={handleNcrLoadHistory}
            onExport={fixture ? undefined : handleNcrExport}
          />
        ) : (
          <div className="placeholder">Loading…</div>
        )
      ) : null}
      {screen === "weekly" ? (
        reportPageLoadError ? (
          <ErrorState
            label="Weekly report"
            errorCode={reportPageLoadError}
            onSignIn={() => auth.login()}
            onRetry={() => loadReportPage(reportViewKey)}
          />
        ) : reportModel ? (
          <Report
            model={reportModel}
            onGenerate={handleReportGenerate}
            pollMs={fixture ? 400 : undefined}
            onJobStatus={handleReportJobStatus}
            me={reportV2 ? V2_ME : fixture ? "sm@example.com" : (auth.getUpn() || "").toLowerCase()}
            onApprove={handleReportApprove}
            onReopen={handleReportReopen}
            onRemindPhoto={handleRemindPhoto}
            onWaivePhoto={handleWaivePhoto}
            onLoadVersions={fixture ? undefined : api.fetchReportVersions}
            onPublish={handleReportPublish}
            onRenderPdf={handleReportRenderPdf}
            onSchedule={handleReportSchedule}
            onHold={handleReportHold}
            canCustomPeriod={identity.role === "Site manager"}
            today={reportV2 ? V2_TODAY : fixture ? planWorld.today : new Date().toISOString().slice(0, 10)}
            periodError={reportPeriodError}
            onSelectPeriod={handleSelectReportPeriod}
            onClearPeriod={handleClearReportPeriod}
            onPrevWeek={() => {
              if (reportPageLoading) return; // ignore a click while a load is already in flight (codex review finding)
              setReportWeekStart((w) => addWeeks(w, -1));
            }}
            onNextWeek={() => {
              if (reportPageLoading) return;
              // The boundary is checked against the WEEK BEING REQUESTED
              // (the updater's own `w`), never the last-loaded model — a
              // burst of rapid clicks before the fetch resolves must still
              // never request a week past the current one (codex review
              // finding; Report.jsx's own `disabled` above is a UI-level
              // backstop, not the actual enforcement).
              setReportWeekStart((w) => clampReportWeekForward(addWeeks(w, 1), currentWeekStart) || w);
            }}
            onBackToCurrent={() => {
              if (reportPageLoading) return;
              setReportWeekStart(currentWeekStart);
            }}
            onNavigate={handleNavigate}
            resolveMissingResult={resolveMissingResult}
            onRefresh={() => loadReportPage(reportViewKey, { fresh: true, quiet: true })}
            onRemindResult={handleRemind}
            weekNavLoading={reportPageLoading}
          />
        ) : (
          <div className="placeholder">Loading…</div>
        )
      ) : null}
      </PhoneShellProvider>
    </div>
  );
}
